/**
 * TrainingPage — Main page for the solo training mode.
 *
 * Layout: 3 columns (controls | game board | trick timeline).
 * Manages socket connection, game state, and all training features.
 */

import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { io, type Socket } from 'socket.io-client';
import Card from '../components/Card';
import TrainingBoard from '../components/TrainingBoard';
import TrainingControls from '../components/TrainingControls';
import TrainingTimeline from '../components/TrainingTimeline';
import SideDrawer from '../components/SideDrawer';
import { SettingsIcon, ListGridIcon, CloseIcon } from '../components/Icons';
import PlayerAvatar from '../components/PlayerAvatar';
import { useResponsive } from '../hooks/useResponsive';
import { useTabReselect } from '../hooks/useTabReselect';
import type { CoachMessage } from '../components/CoachChat';
import type { MLSuggestion, FeedbackSubmitData } from '../components/ExpertFeedbackPanel';
import type { Session, TrainingAIModelSelection, TrainingDealConstraints } from '../types/app';
import type { TrainingDisplayMode } from '../types/app';
import type { CardNumber } from '../types/game';
import { useApp } from '../contexts/AppContext';
import AddToLibraryModal from '../components/library/AddToLibraryModal';
import GameCommentsPanel from '../components/library/GameCommentsPanel';
import PollPanel, { PollVoteBanner, PollAfterVoteModal } from '../components/library/PollPanel';
import { getCardName } from '../utils/cards';
import type { PollResponse, PollVoteType } from '../types/library';
import type { CommentContext } from '../components/library/GameCommentsPanel';
import { setBetaPageDetail } from '../utils/betaFeedbackContext';
import type { GameHistory } from '../game/gameHistory';
import { Contract } from '../types/game';
import '../styles/training.css';
import '../styles/expert-feedback.css';
import '../styles/library.css';
import { BACKEND_URL } from '../config';
import {
  buildGameHistoryFromTrainingGameData,
  compareReplayBatchHasDealList,
  enrichCompareReplayBatchWithTeams,
  gameHistoryToTrainingGameData,
  resolveCompareReplayDealPayload,
  type CompareReplayDealEntry,
} from '../utils/competitionReplay';
import ReplayPane from '../components/ReplayPane';
import TrainingTeamTableHeader from '../components/TrainingTeamTableHeader';
import { cursorFromLiveProgress, resolveCursor, type ReplayCursor } from '../game/replayCursor';
import {
  buildTrainingHistoryTitle,
  buildDiffereNavigationRequest,
  canOpenDiffereSource,
  countTrainingHistory,
  createTrainingHistoryId,
  deleteTrainingHistoryEntry,
  enrichHistoryContext,
  formatTrainingHistoryDate,
  formatTrainingHistoryDisplay,
  listTrainingHistory,
  upsertTrainingHistoryEntry,
  type TrainingHistoryContext,
  type TrainingHistoryEntry,
  type TrainingHistoryFilter,
  type TrainingHistoryLoadRequest,
  type TrainingHistorySource,
  type TrainingHistoryStatus,
} from '../utils/trainingHistory';
import {
  buildDiffereCompareReplayBatch,
  differeCompareReplayBatchNeedsRebuild,
  resolveDiffereBatchTitle,
} from '../utils/differeTrainingBatch';
import TrainingDealSetup from '../components/TrainingDealSetup';
import {
  loadTrainingDealSetup,
  saveTrainingDealSetup,
  toTrainingDealConstraints,
  validateHandQualities,
  type TrainingDealSetupValue,
} from '../utils/trainingDealSetup';

export type GameSpeed = 'fast' | 'normal' | 'slow' | 'click';

interface MistakeExerciseCard {
  id: string;
  differeId: number;
  resultId: number;
  dealIndex: number;
  dealNum: number;
  scoreDelta: number;
  trickIndex: number;
  effectTrickIndex?: number;
  kind: string;
  bestResultId: number;
  bestPseudo: string;
  dealScore: number | null;
  bestScore: number;
  contractLabel: string;
  differeDebut: string | null;
  pseudo: string;
  pseudoDisplay: string | null;
}

/** Liste vide après scan : pas assez de jeu vs aucune erreur coûteuse. */
type MistakesEmptyKind = 'no_recent_play' | 'clean_record';

const TRICK_DISPLAY_MS: Record<GameSpeed, number> = {
  fast: 800,
  normal: 1500,
  slow: 2500,
  click: 0,
};

type ReplayBid = { player: number; bid: number };

/** Données drill métrique (replay comparateur ML) — cartes / plis à mettre en évidence */
type MetricDrillPayload = {
  metric: string;
  highlights: Array<{ trickNumber: number; positionInTrick: number; player: number; card: number }>;
};

type ReviewPhaseState =
  | { type: 'chien' | 'ecart'; cards: CardNumber[] }
  | { type: 'bids'; bids: ReplayBid[] }
  | null;

type PreservedReplayReviewSnapshot = {
  trickNumber: number | null;
  phase: ReviewPhaseState;
};

function replayBidsFromGameData(gameData: unknown): ReplayBid[] {
  const bids = (gameData as { bids?: ReplayBid[] } | null)?.bids;
  return Array.isArray(bids) ? bids : [];
}

function snapshotCompareReplayBatchForHistory(
  batch: {
    title: string;
    deals: Array<
      CompareReplayDealEntry & {
        replaySeatLabels: string[];
        metricDrill?: MetricDrillPayload;
      }
    >;
    selectedIndex: number;
    selectedVariantIndex: number;
  },
  gameData: ReturnType<typeof gameHistoryToTrainingGameData>,
  replaySeatLabels: string[],
) {
  const idx = batch.selectedIndex;
  const updatedDeals = batch.deals.map((d, i) => {
    if (i !== idx) return d;
    if (d.variants && d.variants.length > 0) {
      return {
        ...d,
        variants: d.variants.map((v, vi) =>
          vi === batch.selectedVariantIndex
            ? { ...v, gameData, replaySeatLabels }
            : v,
        ),
      };
    }
    return { ...d, gameData, replaySeatLabels };
  });

  return {
    title: batch.title,
    deals: updatedDeals.map((d) => ({
      dealIndex: d.dealIndex,
      label: d.label,
      gameData: d.gameData,
      replaySeatLabels: d.replaySeatLabels,
      partieId: d.partieId,
      variants: d.variants,
      metricDrill: d.metricDrill,
    })),
    selectedIndex: batch.selectedIndex,
    selectedVariantIndex: batch.selectedVariantIndex,
  };
}

function applyPreservedReplayReview(
  snapshot: PreservedReplayReviewSnapshot,
  completedTricks: Array<{ cards: CardNumber[]; players: number[] }>,
  bidsForReview: ReplayBid[],
  chien: CardNumber[] | undefined,
  ecartCards: CardNumber[],
  mode: string | undefined,
  setReviewTrick: (v: { cards: CardNumber[]; players: number[]; trickNumber: number } | null) => void,
  setReviewPhase: (v: ReviewPhaseState) => void,
): void {
  if (snapshot.trickNumber != null) {
    const ct = completedTricks[snapshot.trickNumber - 1];
    if (ct?.cards?.length) {
      setReviewTrick({
        trickNumber: snapshot.trickNumber,
        cards: ct.cards,
        players: ct.players,
      });
      setReviewPhase(null);
      return;
    }
  }

  setReviewTrick(null);
  if (snapshot.phase?.type === 'bids') {
    setReviewPhase({
      type: 'bids',
      bids: bidsForReview.length > 0 ? bidsForReview : snapshot.phase.bids,
    });
  } else if (snapshot.phase?.type === 'chien') {
    setReviewPhase({
      type: 'chien',
      cards: chien?.length ? chien : snapshot.phase.cards,
    });
  } else if (snapshot.phase?.type === 'ecart') {
    setReviewPhase({
      type: 'ecart',
      cards: ecartCards.length > 0 ? ecartCards : snapshot.phase.cards,
    });
  } else if (mode === 'replay' && bidsForReview.length > 0) {
    setReviewPhase({ type: 'bids', bids: bidsForReview });
  } else {
    setReviewPhase(null);
  }
}

type LibraryGameSource = 'own' | 'shared';

interface TrainingPageProps {
  session: Session;
  isAdmin?: boolean;
}

// Game state from the training engine
interface TrainingGameState {
  sessionId: string;
  phase: string;
  dealNumber: number;
  dealer: number;
  currentPlayer: number;
  myPosition: number;
  myHand: CardNumber[];
  allHands: Record<number, CardNumber[]>;
  legalCards: Record<number, CardNumber[]>;
  players: Array<{ pseudo: string; position: number; isBot: boolean; isConnected: boolean }>;
  bids: Array<{ player: number; bid: number }>;
  highestBid: number;
  taker: number | null;
  chien: CardNumber[] | null;
  currentTrick: CardNumber[];
  trickPlayerPositions: number[];
  numPli: number;
  completedTricks: Array<{ cards: CardNumber[]; players: number[]; winner?: number }>;
  scores: any | null;
}

const INITIAL_STATE: TrainingGameState = {
  sessionId: '',
  phase: 'waiting',
  dealNumber: 0,
  dealer: 0,
  currentPlayer: 0,
  myPosition: 0,
  myHand: [],
  allHands: {},
  legalCards: {},
  players: [],
  bids: [],
  highestBid: 0,
  taker: null,
  chien: null,
  currentTrick: [],
  trickPlayerPositions: [],
  numPli: 0,
  completedTricks: [],
  scores: null,
};

export default function TrainingPage({ session, isAdmin }: TrainingPageProps) {
  const {
    startTraining,
    stopTraining,
    preferences,
    activeTab,
    setActiveTab,
    trainingLeftColumnHidden,
    setTrainingLeftColumnHidden,
    setTrainingFeltActive,
    navigateToDiffere,
  } = useApp();
  const { isCompact } = useResponsive();

  const [gameState, setGameState] = useState<TrainingGameState>(INITIAL_STATE);
  const [isActive, setIsActive] = useState(false);
  useEffect(() => {
    setTrainingFeltActive(isActive);
    return () => setTrainingFeltActive(false);
  }, [isActive, setTrainingFeltActive]);
  const [displayMode, setDisplayMode] = useState<TrainingDisplayMode>('classic');
  const [showTimeline, setShowTimeline] = useState(true);
  const [disabledBots, setDisabledBots] = useState<Set<number>>(new Set());
  const [humanAutomated, setHumanAutomated] = useState(false);
  const [modelNames, setModelNames] = useState<{ attacker: string; defender: string }>({ attacker: 'IA Attaque', defender: 'IA Défense' });
  const [isWaitingRebid, setIsWaitingRebid] = useState(false);
  const [activePosition, setActivePosition] = useState(0);
  const [viewRotation, setViewRotation] = useState(0);
  const [showPositionPicker, setShowPositionPicker] = useState(false);
  const [reviewTrick, setReviewTrick] = useState<{
    cards: CardNumber[]; players: number[]; trickNumber: number;
  } | null>(null);
  const [randomFallbackCards, setRandomFallbackCards] = useState<Set<CardNumber>>(new Set());
  const [forcedBids, setForcedBids] = useState<Record<number, number>>({});
  const [lastCompletedTrick, setLastCompletedTrick] = useState<{
    cards: CardNumber[]; playerPositions: number[];
  } | null>(null);
  const [mlToast, setMlToast] = useState<{
    type: 'error' | 'success'; exiting?: boolean;
  } | null>(null);
  const [showChienOverlay, setShowChienOverlay] = useState(false);
  const [ecartSelectedCards, setEcartSelectedCards] = useState<CardNumber[]>([]);
  const [ecartCards, setEcartCards] = useState<CardNumber[]>([]);
  const [showEndOfDealMenu, setShowEndOfDealMenu] = useState(false);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [libraryPartialSave, setLibraryPartialSave] = useState(false);
  const [libraryGameId, setLibraryGameId] = useState<number | null>(null);
  const [libraryGameSource, setLibraryGameSource] = useState<LibraryGameSource>('own');
  const [libraryCommentsEnabled, setLibraryCommentsEnabled] = useState(true);
  /** Replay bibliothèque / ML compare : noms de sièges fournis, ne pas les remplacer par les IA déployées. */
  const [libraryReplaySeatLabelsFromPayload, setLibraryReplaySeatLabelsFromPayload] = useState(false);
  const [commentingContext, setCommentingContext] = useState<{
    type: 'game' | 'trick' | 'card' | 'bid';
    trickNum?: number;
    cardIndex?: number;
    bidIndex?: number;
    label?: string;
  } | null>(null);
  const [pollMode, setPollMode] = useState<{
    sharedGameId: number;
    sharedBy: string;
    partialPhase?: number;
    voteSeat?: number;
  } | null>(null);
  const [pollHasVoted, setPollHasVoted] = useState(false);
  const [pollAfterVote, setPollAfterVote] = useState<{
    voteType: PollVoteType;
    voteLabel: string;
    responses: PollResponse[];
  } | null>(null);
  const [historyEntries, setHistoryEntries] = useState<TrainingHistoryEntry[]>([]);
  const [historyFilter, setHistoryFilter] = useState<TrainingHistoryFilter>('in_progress');
  const [historyResumeLoading, setHistoryResumeLoading] = useState(false);
  const [finishedHistoryCount, setFinishedHistoryCount] = useState(0);
  const [homeTab, setHomeTab] = useState<'libre' | 'exercices' | 'historique'>('libre');
  const [mistakeExercises, setMistakeExercises] = useState<MistakeExerciseCard[]>([]);
  const [mistakeLoading, setMistakeLoading] = useState(false);
  const [mistakeError, setMistakeError] = useState<string | null>(null);
  const [mistakeHasMore, setMistakeHasMore] = useState(false);
  const [mistakeOffset, setMistakeOffset] = useState(0);
  const [mistakeFetchedFor, setMistakeFetchedFor] = useState<string | null>(null);
  const [mistakeEmptyKind, setMistakeEmptyKind] = useState<MistakesEmptyKind | null>(null);
  const [mistakeCoverage, setMistakeCoverage] = useState<{
    scannedDeals: number;
    tournamentsConsidered: number;
    days: number;
  } | null>(null);
  const [mistakeOpeningId, setMistakeOpeningId] = useState<string | null>(null);
  const [bifurcationTricks, setBifurcationTricks] = useState<Set<number>>(() => new Set());
  const [exerciseMistakeMode, setExerciseMistakeMode] = useState(false);
  const [showReferencePane, setShowReferencePane] = useState(false);
  const [revealReference, setRevealReference] = useState(false);

  /** Liste de replays comparateur ML (plusieurs donnes) — navigation sans repasser par l’admin */
  const [compareReplayBatch, setCompareReplayBatch] = useState<{
    title: string;
    deals: Array<
      CompareReplayDealEntry & {
        replaySeatLabels: string[];
        metricDrill?: MetricDrillPayload;
      }
    >;
    selectedIndex: number;
    selectedVariantIndex: number;
  } | null>(null);

  const initialHandsRef = useRef<Record<number, CardNumber[]>>({});
  const libraryLoadInitialHandsRef = useRef<Record<number, CardNumber[]> | null>(null);
  /** Enchères du gameData client (fallback si le backend ne les renvoie pas au library_loaded). */
  const pendingReplayBidsRef = useRef<ReplayBid[] | null>(null);
  /** Évite de repositionner plusieurs fois la revue sur le premier pli drill pour la même donne */
  const metricDrillJumpKeyRef = useRef<string | null>(null);
  /** Conserve l’étape de revue (pli / enchères / chien…) lors d’un changement de table duplicate. */
  const preservedReviewSnapshotRef = useRef<PreservedReplayReviewSnapshot | null>(null);
  /** Mémoire de consultation par donne (match / batch) — oubliée à la fermeture. */
  const dealReviewMemoryRef = useRef<Map<number, PreservedReplayReviewSnapshot>>(new Map());
  const reviewTrickRef = useRef<{
    cards: CardNumber[]; players: number[]; trickNumber: number;
  } | null>(null);
  const reviewPhaseRef = useRef<ReviewPhaseState>(null);
  const [reviewPhase, setReviewPhase] = useState<ReviewPhaseState>(null);
  reviewTrickRef.current = reviewTrick;
  reviewPhaseRef.current = reviewPhase;
  const [gameSpeed, setGameSpeed] = useState<GameSpeed>('normal');
  const [dealSetup, setDealSetup] = useState<TrainingDealSetupValue>(() =>
    loadTrainingDealSetup(session.pseudo),
  );
  const [reviewAnimIndex, setReviewAnimIndex] = useState(0);
  const [awaitingAdvance, setAwaitingAdvance] = useState(false);
  const [coachMessages, setCoachMessages] = useState<CoachMessage[]>([]);
  const coachIdCounter = useRef(0);
  const [adviceDetailed, setAdviceDetailed] = useState(true);
  const [feedbackMode, setFeedbackMode] = useState(false);
  const [feedbackEnabledSeats, setFeedbackEnabledSeats] = useState<Set<number>>(() => {
    return new Set([0, 1, 2, 3]);
  });
  const [botModelSlot, setBotModelSlot] = useState<'production' | 'beta'>(
    preferences.useBetaModel ? 'beta' : 'production',
  );
  const [aiModelSelection, setAIModelSelection] = useState<TrainingAIModelSelection>({
    attacker: preferences.useBetaModel ? 'beta' : 'production',
    defender: preferences.useBetaModel ? 'beta' : 'production',
    bidding: preferences.useBetaModel ? 'beta' : 'production',
    discard: preferences.useBetaModel ? 'beta' : 'production',
  });
  const [mlSuggestion, setMlSuggestion] = useState<MLSuggestion | null>(null);
  const [bidComparison, setBidComparison] = useState<{
    seat: number; humanBid: number; mlBid: number; mlBidName: string;
    probabilities: Record<string, number>; source: string; agrees: boolean;
  } | null>(null);
  const [discardComparison, setDiscardComparison] = useState<{
    seat: number; humanDiscard: number[]; mlDiscard: number[];
    mlDiscardNames: string[]; source: string; overlap: number; agrees: boolean;
  } | null>(null);
  const [highlightedCard, setHighlightedCard] = useState<CardNumber | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;
  const currentHistoryEntryIdRef = useRef<string | null>(null);
  const currentHistoryCreatedAtRef = useRef<number | null>(null);
  const trainingSessionModeRef = useRef<'replay' | 'play'>('play');
  const historyContextRef = useRef<TrainingHistoryContext | undefined>(undefined);
  const saveSessionRef = useRef<(status: TrainingHistoryStatus) => void>(() => {});
  const feedbackModeRef = useRef(feedbackMode);
  feedbackModeRef.current = feedbackMode;
  const mlToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mlDisconnectedRef = useRef(false);
  const socketRef = useRef<Socket | null>(null);
  const trickDisplayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gameSpeedRef = useRef(gameSpeed);
  gameSpeedRef.current = gameSpeed;

  useEffect(() => {
    if (!isActive || activeTab !== 'training') {
      setBetaPageDetail(null);
      return;
    }
    setBetaPageDetail({
      page: 'training',
      phase: gameState.phase,
      dealNumber: gameState.dealNumber,
      sessionId: gameState.sessionId || null,
      playerCount: gameState.players.length,
      numPli: gameState.numPli,
      currentContract: gameState.highestBid > 0 ? gameState.highestBid : null,
      displayMode,
      showTimeline,
      pollMode: pollMode
        ? {
            sharedGameId: pollMode.sharedGameId,
            partialPhase: pollMode.partialPhase ?? null,
          }
        : null,
      libraryReplay: libraryGameId != null,
      compareReplayBatch: compareReplayBatch
        ? {
            title: compareReplayBatch.title,
            dealCount: compareReplayBatch.deals.length,
            selectedIndex: compareReplayBatch.selectedIndex,
          }
        : null,
    });
    return () => setBetaPageDetail(null);
  }, [
    activeTab,
    compareReplayBatch,
    displayMode,
    gameState,
    isActive,
    libraryGameId,
    pollMode,
    showTimeline,
  ]);

  // Connect socket on mount, with reconnection support
  useEffect(() => {
    const socket = io(BACKEND_URL, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;
    let firstConnect = true;

    socket.on('connect', () => {
      socket.emit('identify', { pseudo: session.pseudo });
      if (!firstConnect && isActiveRef.current) {
        socket.emit('training:rejoin', { pseudo: session.pseudo });
      }
      firstConnect = false;
    });

    socket.on('training:rejoined', (data: any) => {
      const state = data.state;
      setGameState({
        sessionId: data.sessionId,
        phase: state.phase,
        dealNumber: state.dealNumber,
        dealer: state.dealer,
        currentPlayer: state.currentPlayer,
        myPosition: 0,
        myHand: state.hand ?? [],
        allHands: data.fullState?.allHands ?? {},
        legalCards: data.fullState?.legalCards ?? {},
        players: state.players ?? [],
        bids: state.bids ?? [],
        highestBid: state.highestBid ?? 0,
        taker: state.taker,
        chien: state.chien,
        currentTrick: state.currentTrick?.cards ?? [],
        trickPlayerPositions: state.currentTrick?.players ?? [],
        numPli: state.numPli ?? 0,
        completedTricks: state.completedTricks?.map((t: any) => ({
          cards: t.cards, players: t.players, winner: t.winner,
        })) ?? [],
        scores: state.scores,
      });
      setIsActive(true);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [session.pseudo]);

  // Wire up training socket events
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const updateFromFullState = (fullState: { allHands: Record<number, CardNumber[]>; legalCards: Record<number, CardNumber[]> }) => {
      setGameState(prev => ({
        ...prev,
        allHands: fullState.allHands,
        legalCards: fullState.legalCards,
      }));
    };

    socket.on('training:created', (data: any) => {
      const state = data.state;
      if (data.modelNames) setModelNames(data.modelNames);
      setLibraryReplaySeatLabelsFromPayload(false);
      currentHistoryEntryIdRef.current = createTrainingHistoryId();
      currentHistoryCreatedAtRef.current = Date.now();
      trainingSessionModeRef.current = 'play';
      historyContextRef.current = { kind: 'solo' };
      setGameState({
        sessionId: data.sessionId,
        phase: state.phase,
        dealNumber: state.dealNumber,
        dealer: state.dealer,
        currentPlayer: state.currentPlayer,
        myPosition: 0,
        myHand: state.hand ?? [],
        allHands: data.fullState?.allHands ?? {},
        legalCards: data.fullState?.legalCards ?? {},
        players: state.players ?? [],
        bids: state.bids ?? [],
        highestBid: state.highestBid ?? 0,
        taker: state.taker,
        chien: state.chien,
        currentTrick: state.currentTrick?.cards ?? [],
        trickPlayerPositions: state.currentTrick?.players ?? [],
        numPli: state.numPli ?? 0,
        completedTricks: [],
        scores: state.scores,
      });
      setIsActive(true);
      setIsWaitingRebid(false);

    });

    socket.on('training:deal', (data: any) => {
      if (data.fullState?.allHands) {
        const snapshot: Record<number, CardNumber[]> = {};
        for (const [pos, hand] of Object.entries(data.fullState.allHands)) {
          snapshot[Number(pos)] = [...(hand as CardNumber[])];
        }
        if (!libraryLoadInitialHandsRef.current) {
          initialHandsRef.current = snapshot;
        }
      }
      setGameState(prev => ({
        ...prev,
        phase: 'bidding',
        dealNumber: data.dealNumber,
        dealer: data.dealer,
        currentPlayer: data.firstBidder,
        myHand: data.hand ?? prev.myHand,
        allHands: data.fullState?.allHands ?? prev.allHands,
        legalCards: data.fullState?.legalCards ?? prev.legalCards,
        bids: [],
        highestBid: 0,
        taker: null,
        chien: null,
        currentTrick: [],
        trickPlayerPositions: [],
        numPli: 0,
        completedTricks: [],
        scores: null,
      }));
      setIsWaitingRebid(false);
      setEcartCards([]);
      setEcartSelectedCards([]);
      setShowChienOverlay(false);
      setShowEndOfDealMenu(false);
      setShowLibraryModal(false);
      setCoachMessages([]);
    });

    socket.on('training:bid_made', (data: any) => {
      if (data.fullState) updateFromFullState(data.fullState);
      setGameState(prev => ({
        ...prev,
        bids: [...prev.bids, { player: data.player, bid: data.bid }],
        highestBid: Math.max(prev.highestBid, data.bid),
        currentPlayer: data.nextPlayer ?? prev.currentPlayer,
        taker: data.bid > 0 && data.bid > prev.highestBid ? data.player : prev.taker,
      }));
    });

    socket.on('training:chien', (data: any) => {
      if (data.fullState) updateFromFullState(data.fullState);
      const hasCards = data.cards && data.cards.length > 0;
      if (hasCards) {
        setShowChienOverlay(true);
        setEcartSelectedCards([]);
      }
      setGameState(prev => {
        const isPlaying = !hasCards;
        return {
          ...prev,
          phase: isPlaying ? 'playing' : 'ecart',
          chien: data.cards,
          taker: data.taker ?? prev.taker,
          ...(isPlaying ? { currentPlayer: (prev.dealer + 1) % 4 } : {}),
        };
      });
    });

    socket.on('training:hand_updated', (data: any) => {
      if (data.fullState) updateFromFullState(data.fullState);
      setGameState(prev => ({ ...prev, myHand: data.hand ?? prev.myHand }));
    });

    socket.on('training:ecart_done', (data: any) => {
      if (data.fullState) updateFromFullState(data.fullState);
      if (data.ecart) setEcartCards(data.ecart);
      setShowChienOverlay(false);
      setEcartSelectedCards([]);
      setGameState(prev => ({
        ...prev,
        phase: 'playing',
        currentPlayer: (prev.dealer + 1) % 4,
      }));
    });

    socket.on('training:card_played', (data: any) => {
      if (data.fullState) updateFromFullState(data.fullState);
      if (data.randomFallback === true) {
        setRandomFallbackCards(prev => new Set(prev).add(data.card));
        mlDisconnectedRef.current = true;
        if (mlToastTimerRef.current) clearTimeout(mlToastTimerRef.current);
        setMlToast({ type: 'error' });
        mlToastTimerRef.current = setTimeout(() => {
          setMlToast(prev => prev?.type === 'error' ? { ...prev, exiting: true } : prev);
          setTimeout(() => setMlToast(prev => prev?.type === 'error' ? null : prev), 300);
        }, 500);
      } else if (data.randomFallback === false) {
        if (mlDisconnectedRef.current) {
          mlDisconnectedRef.current = false;
          if (mlToastTimerRef.current) clearTimeout(mlToastTimerRef.current);
          setMlToast({ type: 'success' });
          mlToastTimerRef.current = setTimeout(() => {
            setMlToast(prev => prev?.type === 'success' ? { ...prev, exiting: true } : prev);
            setTimeout(() => setMlToast(prev => prev?.type === 'success' ? null : prev), 300);
          }, 500);
        }
      }
      if (data.rewindSeatCleared !== undefined) {
        setDisabledBots(prev => {
          const next = new Set(prev);
          next.delete(data.rewindSeatCleared);
          return next;
        });
      }
      setGameState(prev => ({
        ...prev,
        currentTrick: [...prev.currentTrick, data.card],
        trickPlayerPositions: [...prev.trickPlayerPositions, data.player],
        currentPlayer: (data.player + 1) % 4,
      }));
      setAwaitingAdvance(false);
      if (!feedbackModeRef.current) {
        setMlSuggestion(null);
      }
    });

    socket.on('training:trick_end', (data: any) => {
      if (data.fullState) updateFromFullState(data.fullState);

      setLastCompletedTrick({ cards: data.cards, playerPositions: data.players });

      setGameState(prev => ({
        ...prev,
        completedTricks: [...prev.completedTricks, {
          cards: data.cards,
          players: data.players,
          winner: data.winner,
        }],
        currentTrick: [],
        trickPlayerPositions: [],
        numPli: prev.numPli + 1,
        currentPlayer: data.winner ?? prev.currentPlayer,
      }));

      if (trickDisplayTimeoutRef.current) clearTimeout(trickDisplayTimeoutRef.current);
      if (gameSpeedRef.current !== 'click') {
        trickDisplayTimeoutRef.current = setTimeout(() => {
          setLastCompletedTrick(null);
        }, TRICK_DISPLAY_MS[gameSpeedRef.current]);
      }
    });

    socket.on('training:scores', (data: any) => {
      if (data.fullState) updateFromFullState(data.fullState);
      if (trickDisplayTimeoutRef.current) {
        clearTimeout(trickDisplayTimeoutRef.current);
        trickDisplayTimeoutRef.current = null;
      }
      setLastCompletedTrick(null);
      setGameState(prev => ({
        ...prev,
        phase: 'end_of_deal',
        scores: data.dealScore,
      }));
      setTimeout(() => saveSessionRef.current('finished'), 50);
    });

    socket.on('training:all_passed', (data: any) => {
      if (data.fullState) updateFromFullState(data.fullState);
    });

    socket.on('training:all_passed_rebid', (data: any) => {
      setIsWaitingRebid(true);
      setGameState(prev => ({
        ...prev,
        chien: data.chien ?? prev.chien,
      }));
    });

    socket.on('training:rebid_started', (data: any) => {
      setIsWaitingRebid(false);
      setGameState(prev => ({
        ...prev,
        phase: 'bidding',
        bids: [],
        highestBid: 0,
        taker: null,
        currentPlayer: data.firstBidder,
      }));
    });

    socket.on('training:awaiting_human_for_bot', (data: any) => {
      if (data.fullState) updateFromFullState(data.fullState);
      setGameState(prev => ({
        ...prev,
        currentPlayer: data.position,
      }));
    });

    socket.on('training:trick_rewound', (data: any) => {
      if (data.fullState) updateFromFullState(data.fullState);
      if (data.tempDisabledSeat !== undefined) {
        setDisabledBots(prev => new Set(prev).add(data.tempDisabledSeat));
      }
      setGameState(prev => ({
        ...prev,
        phase: data.phase ?? 'playing',
        currentTrick: data.currentTrick ?? [],
        trickPlayerPositions: data.trickPlayers ?? [],
        currentPlayer: data.currentPlayer,
        numPli: data.numPli ?? prev.numPli,
        completedTricks: data.completedTricks ?? prev.completedTricks,
        scores: data.phase === 'playing' ? undefined : prev.scores,
      }));
      setLastCompletedTrick(null);
      setReviewTrick(null);
      setShowEndOfDealMenu(false);
    });

    socket.on('training:new_deal_started', (data: any) => {
      const state = data.state;
      currentHistoryEntryIdRef.current = createTrainingHistoryId();
      currentHistoryCreatedAtRef.current = Date.now();
      setLastCompletedTrick(null);
      setRandomFallbackCards(new Set());
      setReviewTrick(null);
      setReviewPhase(null);
      setForcedBids({});
      setShowChienOverlay(false);
      setEcartSelectedCards([]);
      setEcartCards([]);
      setShowEndOfDealMenu(false);
      setLibraryGameId(null);
      setCommentingContext(null);
      setPollMode(null);
      if (trickDisplayTimeoutRef.current) clearTimeout(trickDisplayTimeoutRef.current);
      setGameState(prev => ({
        ...prev,
        phase: state.phase,
        dealNumber: state.dealNumber,
        dealer: state.dealer ?? prev.dealer,
        currentPlayer: state.currentPlayer ?? prev.currentPlayer,
        myHand: state.hand,
        allHands: data.fullState?.allHands ?? {},
        legalCards: data.fullState?.legalCards ?? {},
        bids: [],
        highestBid: 0,
        taker: null,
        chien: null,
        currentTrick: [],
        trickPlayerPositions: [],
        numPli: 0,
        completedTricks: [],
        scores: null,
      }));
      setIsWaitingRebid(false);
    });

    socket.on('training:bot_toggled', (data: any) => {
      setDisabledBots(prev => {
        const next = new Set(prev);
        if (data.enabled) {
          next.delete(data.seat);
        } else {
          next.add(data.seat);
        }
        return next;
      });
    });

    socket.on('training:bid_forced', (data: any) => {
      setForcedBids(prev => ({ ...prev, [data.seat]: data.contract }));
    });

    socket.on('training:state_update', (data: any) => {
      if (data.fullState) updateFromFullState(data.fullState);
      setGameState(prev => ({
        ...prev,
        ...(data.phase ? { phase: data.phase } : {}),
        ...(data.currentPlayer !== undefined ? { currentPlayer: data.currentPlayer } : {}),
      }));
    });

    socket.on('training:abandoned', () => {
      dealReviewMemoryRef.current.clear();
      setShowReferencePane(false);
      setCompareReplayBatch(null);
      setIsActive(false);
      setGameState(INITIAL_STATE);
      stopTraining();
    });

    socket.on('training:error', (data: any) => {
      console.warn('[Training] Error:', data.message);
    });

    socket.on('training:awaiting_advance', () => {
      setAwaitingAdvance(true);
    });

    socket.on('training:ml_suggestion', (data: MLSuggestion) => {
      setMlSuggestion(data);
    });

    socket.on('training:feedback_submitted', () => {
      // Feedback acknowledged by backend (no action needed)
    });

    socket.on('training:bid_comparison', (data: any) => {
      setBidComparison(data);
      setTimeout(() => setBidComparison(null), 12000);
    });

    socket.on('training:discard_comparison', (data: any) => {
      setDiscardComparison(data);
      setTimeout(() => setDiscardComparison(null), 15000);
    });

    socket.on('training:library_loaded', (data: any) => {
      const state = data.state;
      if (libraryLoadInitialHandsRef.current) {
        initialHandsRef.current = libraryLoadInitialHandsRef.current;
        libraryLoadInitialHandsRef.current = null;
      }
      if (data.modelNames) setModelNames(data.modelNames);
      setLibraryReplaySeatLabelsFromPayload(data.replayAllBots === true);
      if (data.deal?.ecart != null && Array.isArray(data.deal.ecart)) {
        setEcartCards([...data.deal.ecart]);
      } else {
        setEcartCards([]);
      }
      if (state) {
        const humanPlayer = state.players?.find((p: any) => !p.isBot);
        const replayAllBots = data.replayAllBots === true;
        const pendingBids = pendingReplayBidsRef.current;
        pendingReplayBidsRef.current = null;
        const bidsForReview =
          Array.isArray(state.bids) && state.bids.length > 0
            ? state.bids
            : (pendingBids ?? []);

        setGameState(prev => ({
          ...prev,
          sessionId: data.sessionId ?? prev.sessionId,
          phase: state.phase ?? prev.phase,
          dealNumber: state.dealNumber ?? prev.dealNumber,
          dealer: state.dealer ?? prev.dealer,
          currentPlayer: state.currentPlayer ?? prev.currentPlayer,
          myPosition: replayAllBots ? 0 : (humanPlayer?.position ?? prev.myPosition),
          myHand: state.hand ?? prev.myHand,
          allHands: data.fullState?.allHands ?? prev.allHands,
          legalCards: data.fullState?.legalCards ?? prev.legalCards,
          players: state.players ?? prev.players,
          bids: bidsForReview.length > 0 ? bidsForReview : (state.bids ?? prev.bids),
          highestBid: state.highestBid ?? prev.highestBid,
          taker: state.taker ?? prev.taker,
          chien: state.chien ?? prev.chien,
          currentTrick: state.currentTrick?.cards ?? prev.currentTrick,
          trickPlayerPositions: state.currentTrick?.players ?? prev.trickPlayerPositions,
          numPli: state.numPli ?? prev.numPli,
          completedTricks: state.completedTricks?.map((t: any) => ({
            cards: t.cards, players: t.players, winner: t.winner,
          })) ?? prev.completedTricks,
          scores: state.scores ?? prev.scores,
        }));
        // Also clear any stale UI state
        setLastCompletedTrick(null);
        setShowEndOfDealMenu(false);
        /** Sinon l’effet drill métrique croit avoir déjà sauté vers le pli (ref inchangée) et ne rouvre pas la revue */
        metricDrillJumpKeyRef.current = null;

        const completedTricks = state.completedTricks?.map((t: any) => ({
          cards: t.cards, players: t.players, winner: t.winner,
        })) ?? [];
        const ecartFromDeal = Array.isArray(data.deal?.ecart) ? [...data.deal.ecart] as CardNumber[] : [];
        const preservedReview = preservedReviewSnapshotRef.current;
        preservedReviewSnapshotRef.current = null;

        if (preservedReview) {
          applyPreservedReplayReview(
            preservedReview,
            completedTricks,
            bidsForReview,
            state.chien,
            ecartFromDeal,
            data.mode,
            setReviewTrick,
            setReviewPhase,
          );
        } else {
          setReviewTrick(null);
          if (data.mode === 'replay' && bidsForReview.length > 0) {
            setReviewPhase({ type: 'bids', bids: bidsForReview });
          } else if (data.mode !== 'replay') {
            setReviewPhase(null);
          }
        }

        // If loaded at ecart phase, show chien overlay so user can dismiss and start ecart
        if (state.phase === 'ecart' && state.chien?.length > 0) {
          setShowChienOverlay(true);
          setEcartSelectedCards([]);
        }
      }
      setIsActive(true);
    });

    socket.on('training:players_updated', (data: { players: Array<{ pseudo: string; position: number; isBot: boolean }> }) => {
      setGameState(prev => ({
        ...prev,
        players: data.players.map(p => ({
          ...p,
          isConnected: prev.players.find(pp => pp.position === p.position)?.isConnected ?? true,
        })),
      }));
    });

    return () => {
      socket.off('training:created');
      socket.off('training:deal');
      socket.off('training:bid_made');
      socket.off('training:chien');
      socket.off('training:hand_updated');
      socket.off('training:ecart_done');
      socket.off('training:card_played');
      socket.off('training:trick_end');
      socket.off('training:scores');
      socket.off('training:all_passed');
      socket.off('training:all_passed_rebid');
      socket.off('training:rebid_started');
      socket.off('training:awaiting_human_for_bot');
      socket.off('training:trick_rewound');
      socket.off('training:new_deal_started');
      socket.off('training:bot_toggled');
      socket.off('training:bid_forced');
      socket.off('training:state_update');
      socket.off('training:abandoned');
      socket.off('training:error');
      socket.off('training:awaiting_advance');
      socket.off('training:ml_suggestion');
      socket.off('training:feedback_submitted');
      socket.off('training:bid_comparison');
      socket.off('training:discard_comparison');
      socket.off('training:library_loaded');
      socket.off('training:players_updated');
    };
  }, [stopTraining]);

  // ============================================
  // Pick up library load requests when training tab becomes active
  // ============================================

  const refreshHistoryList = useCallback((filter: TrainingHistoryFilter = historyFilter) => {
    setHistoryEntries(listTrainingHistory(session.pseudo, { filter }));
    setFinishedHistoryCount(countTrainingHistory(session.pseudo, 'finished'));
  }, [session.pseudo, historyFilter]);

  useEffect(() => {
    if (!isActive) refreshHistoryList();
  }, [isActive, refreshHistoryList]);

  useEffect(() => {
    setDealSetup(loadTrainingDealSetup(session.pseudo));
  }, [session.pseudo]);

  useEffect(() => {
    if (!isActive) refreshHistoryList(historyFilter);
  }, [historyFilter, isActive, refreshHistoryList]);

  const applyTrainingLoadRequest = useCallback((
    request: TrainingHistoryLoadRequest & {
      replayAllBots?: boolean;
      replaySeatLabels?: string[];
      pollVoteMode?: boolean;
      historyContext?: TrainingHistoryContext;
    },
    options?: { preserveHistoryEntry?: boolean },
  ) => {
    const socket = socketRef.current;
    if (!socket) return;

    if (!options?.preserveHistoryEntry) {
      currentHistoryEntryIdRef.current = createTrainingHistoryId();
      currentHistoryCreatedAtRef.current = Date.now();
    }
    dealReviewMemoryRef.current.clear();
    trainingSessionModeRef.current = request.mode;
    historyContextRef.current = request.historyContext;

    const bifTricks = Array.isArray(request.bifurcationTricks)
      ? request.bifurcationTricks.filter((n): n is number => Number.isFinite(Number(n))).map(Number)
      : [];
    setBifurcationTricks(new Set(bifTricks));
    const isExerciseMistake = Boolean(request.historyContext?.differe?.exerciseMistake);
    setExerciseMistakeMode(isExerciseMistake);
    if (isExerciseMistake || bifTricks.length > 0) {
      setShowReferencePane(true);
      setRevealReference(true);
    }

    const libraryId = request.libraryGameId ?? null;
    setLibraryGameId(libraryId);
    setLibraryGameSource(request.libraryGameSource ?? 'own');
    setLibraryCommentsEnabled(request.commentsEnabled ?? true);
    setCommentingContext(null);

    const batchRaw = request.compareReplayBatch;

    let gameDataToLoad: unknown = request.gameData;
    let replaySeatLabelsToLoad: string[] | undefined =
      Array.isArray(request.replaySeatLabels) ? request.replaySeatLabels : undefined;
    let selectedVariantIndex = 0;

    if (batchRaw?.deals && batchRaw.deals.length > 0) {
      const sel = Math.min(
        Math.max(0, Number(batchRaw.selectedIndex) || 0),
        batchRaw.deals.length - 1,
      );
      const deal = batchRaw.deals[sel];
      selectedVariantIndex = Math.min(
        Math.max(0, Number(batchRaw.selectedVariantIndex) || 0),
        Math.max(0, (deal?.variants?.length ?? 1) - 1),
      );
      const payload = deal ? resolveCompareReplayDealPayload(deal, selectedVariantIndex) : null;
      gameDataToLoad = payload?.gameData;
      replaySeatLabelsToLoad =
        payload && payload.replaySeatLabels.length === 4
          ? payload.replaySeatLabels
          : replaySeatLabelsToLoad;

      const mappedBatch = {
        title:
          typeof batchRaw.title === 'string' && batchRaw.title.length > 0
            ? batchRaw.title
            : 'Donnes (comparateur ML)',
        deals: batchRaw.deals.map((d, i) => {
          const deal = d as CompareReplayDealEntry & {
            replaySeatLabels?: string[];
            metricDrill?: MetricDrillPayload;
          };
          return {
            dealIndex: typeof deal.dealIndex === 'number' ? deal.dealIndex : i,
            label:
              typeof deal.label === 'string' && deal.label.length > 0
                ? deal.label
                : `Donne ${typeof deal.dealIndex === 'number' ? deal.dealIndex : i + 1}`,
            gameData: deal.gameData,
            replaySeatLabels: Array.isArray(deal.replaySeatLabels) ? deal.replaySeatLabels : [],
            partieId: typeof deal.partieId === 'number' ? deal.partieId : undefined,
            variants: Array.isArray(deal.variants) ? deal.variants : undefined,
            metricDrill:
              deal.metricDrill && typeof deal.metricDrill.metric === 'string'
                ? {
                    metric: deal.metricDrill.metric,
                    highlights: Array.isArray(deal.metricDrill.highlights)
                      ? deal.metricDrill.highlights
                      : [],
                  }
                : undefined,
          };
        }),
        selectedIndex: sel,
        selectedVariantIndex,
      };
      setCompareReplayBatch(
        enrichCompareReplayBatchWithTeams(mappedBatch, request.historyContext?.teamChamp),
      );
    } else {
      setCompareReplayBatch(null);
    }

    const initialHands: Record<number, CardNumber[]> = {};
    for (const [pos, hand] of Object.entries((gameDataToLoad as { initialHands?: Record<string, CardNumber[]> })?.initialHands ?? {})) {
      initialHands[Number(pos)] = [...(hand as CardNumber[])];
    }
    initialHandsRef.current = initialHands;
    libraryLoadInitialHandsRef.current = initialHands;
    const replayBids = request.mode === 'replay' ? replayBidsFromGameData(gameDataToLoad) : [];
    pendingReplayBidsRef.current = replayBids.length > 0 ? replayBids : null;
    setReviewTrick(null);
    if (replayBids.length > 0) {
      setReviewPhase({ type: 'bids', bids: replayBids });
      setGameState(prev => ({ ...prev, bids: replayBids }));
    } else {
      setReviewPhase(null);
    }
    if (request.replayAllBots === true && request.mode === 'replay') {
      setDisplayMode('cards_on_table');
      setHumanAutomated(true);
      setDisabledBots(new Set());
    }
    setIsActive(true);
    socket.emit('training:load_library_game', {
      pseudo: session.pseudo,
      gameData: gameDataToLoad,
      mode: request.mode,
      position: request.position ?? 0,
      biddingMode: request.biddingMode,
      isPartial: request.isPartial ?? false,
      partialPhase: request.partialPhase,
      partialTrickNum: request.partialTrickNum,
      speed: request.mode === 'replay' ? 'normal' : gameSpeedRef.current,
      compet: session.compet ?? false,
      replayAllBots: request.replayAllBots === true,
      replaySeatLabels: replaySeatLabelsToLoad,
      pollVoteMode: request.pollVoteMode === true,
    });
  }, [session.pseudo, session.compet]);

  useEffect(() => {
    if (activeTab !== 'training') return;

    const pollStored = sessionStorage.getItem('library_poll_game');
    if (pollStored) {
      sessionStorage.removeItem('library_poll_game');
      try {
        const pollData = JSON.parse(pollStored);
        setPollMode({
          sharedGameId: pollData.sharedGameId,
          sharedBy: pollData.sharedBy,
          partialPhase: pollData.partialPhase,
          voteSeat: pollData.position ?? 0,
        });
        setPollHasVoted(false);
        setPollAfterVote(null);
      } catch { /* ignore */ }
    }

    const stored = sessionStorage.getItem('library_load_request');
    if (!stored) return;

    const socket = socketRef.current;
    if (!socket) return;

    try {
      if (isActiveRef.current) {
        saveSessionRef.current('in_progress');
      }
      const request = JSON.parse(stored);
      sessionStorage.removeItem('library_load_request');
      applyTrainingLoadRequest(request);
    } catch { /* ignore */ }
  }, [activeTab, applyTrainingLoadRequest]);

  // ============================================
  // Actions
  // ============================================

  const emitCompareReplayDeal = useCallback((
    gameData: unknown,
    replaySeatLabels: string[],
    options?: { preserveReview?: boolean },
  ) => {
    const socket = socketRef.current;
    if (!socket) return;

    const initialHands: Record<number, CardNumber[]> = {};
    for (const [pos, hand] of Object.entries(
      (gameData as { initialHands?: Record<string, CardNumber[]> })?.initialHands ?? {},
    )) {
      initialHands[Number(pos)] = [...(hand as CardNumber[])];
    }
    initialHandsRef.current = initialHands;
    libraryLoadInitialHandsRef.current = initialHands;

    const replayBids = replayBidsFromGameData(gameData);
    pendingReplayBidsRef.current = replayBids.length > 0 ? replayBids : null;
    const nextEcart = Array.isArray((gameData as { ecart?: CardNumber[] })?.ecart)
      ? [...(gameData as { ecart: CardNumber[] }).ecart]
      : [];
    setEcartCards(nextEcart);
    if (!options?.preserveReview) {
      setReviewTrick(null);
      if (replayBids.length > 0) {
        setReviewPhase({ type: 'bids', bids: replayBids });
        setGameState(prev => ({ ...prev, bids: replayBids }));
      } else {
        setReviewPhase(null);
      }
    }
    setCommentingContext(null);
    setLibraryGameId(null);

    socket.emit('training:load_library_game', {
      pseudo: session.pseudo,
      gameData,
      mode: 'replay',
      position: 0,
      speed: 'normal',
      compet: session.compet ?? false,
      replayAllBots: true,
      replaySeatLabels: replaySeatLabels.length === 4 ? replaySeatLabels : undefined,
    });
  }, [session.pseudo, session.compet]);

  const handleSelectCompareReplayDeal = useCallback((index: number, variantIndex?: number) => {
    if (!compareReplayBatch) return;
    saveSessionRef.current('in_progress');
    const deal = compareReplayBatch.deals[index];
    if (!deal) return;

    const linkedTables = (deal.variants?.length ?? 0) >= 2;
    /** En dual lié, la colonne gauche du tableau = toujours variants[0]. */
    const forceLeftColumn = showReferencePane && linkedTables;

    let nextVariantIndex = variantIndex;
    if (forceLeftColumn) {
      nextVariantIndex = 0;
    } else if (nextVariantIndex == null) {
      if (index !== compareReplayBatch.selectedIndex) {
        const prevDeal = compareReplayBatch.deals[compareReplayBatch.selectedIndex];
        const prevTableIdx =
          prevDeal?.variants?.[compareReplayBatch.selectedVariantIndex]?.tableIdx;
        if (prevTableIdx != null && deal.variants?.length) {
          const matching = deal.variants.findIndex((v) => v.tableIdx === prevTableIdx);
          nextVariantIndex = matching >= 0 ? matching : 0;
        } else {
          nextVariantIndex = 0;
        }
      } else {
        nextVariantIndex = compareReplayBatch.selectedVariantIndex;
      }
    }

    const payload = resolveCompareReplayDealPayload(deal, nextVariantIndex);
    if (!payload) return;

    const isSameDonneTableSwitch =
      index === compareReplayBatch.selectedIndex
      && nextVariantIndex !== compareReplayBatch.selectedVariantIndex;

    const isDealSwitch = index !== compareReplayBatch.selectedIndex;
    const currentSnapshot: PreservedReplayReviewSnapshot = {
      trickNumber: reviewTrickRef.current?.trickNumber ?? null,
      phase: reviewPhaseRef.current,
    };

    if (isSameDonneTableSwitch) {
      preservedReviewSnapshotRef.current = currentSnapshot;
    } else if (isDealSwitch) {
      const prevDeal = compareReplayBatch.deals[compareReplayBatch.selectedIndex];
      if (prevDeal) {
        dealReviewMemoryRef.current.set(prevDeal.dealIndex, currentSnapshot);
      }
      const remembered = dealReviewMemoryRef.current.get(deal.dealIndex) ?? null;
      preservedReviewSnapshotRef.current = remembered;
    } else {
      preservedReviewSnapshotRef.current = null;
    }

    setCompareReplayBatch(prev =>
      prev
        ? { ...prev, selectedIndex: index, selectedVariantIndex: nextVariantIndex }
        : null,
    );
    if (!isSameDonneTableSwitch) {
      metricDrillJumpKeyRef.current = null;
    }
    emitCompareReplayDeal(payload.gameData, payload.replaySeatLabels, {
      preserveReview: isSameDonneTableSwitch || (isDealSwitch && preservedReviewSnapshotRef.current != null),
    });
  }, [compareReplayBatch, emitCompareReplayDeal, showReferencePane]);

  const activeMetricDrill = useMemo(
    () =>
      compareReplayBatch?.deals[compareReplayBatch.selectedIndex]?.metricDrill ?? null,
    [compareReplayBatch],
  );

  const metricHighlightKeySet = useMemo(() => {
    const s = new Set<string>();
    for (const ev of activeMetricDrill?.highlights ?? []) {
      s.add(`${ev.trickNumber}-${ev.positionInTrick}-${ev.card}`);
    }
    return s;
  }, [activeMetricDrill]);

  const metricHighlightTrickNumbers = useMemo(() => {
    const s = new Set<number>();
    for (const ev of activeMetricDrill?.highlights ?? []) s.add(ev.trickNumber);
    return s;
  }, [activeMetricDrill]);

  /** Numéro du pli affiché au centre de la table (revue, pli en cours ou dernier pli figé) */
  const metricTableTrickNumber = useMemo(() => {
    if (!activeMetricDrill?.highlights?.length) return null;
    if (reviewTrick) return reviewTrick.trickNumber;
    if (gameState.currentTrick.length > 0) return gameState.numPli + 1;
    if (lastCompletedTrick && gameState.currentTrick.length === 0)
      return gameState.completedTricks.length;
    return null;
  }, [
    activeMetricDrill,
    reviewTrick,
    gameState.currentTrick.length,
    gameState.numPli,
    lastCompletedTrick,
    gameState.completedTricks.length,
  ]);

  useEffect(() => {
    if (!compareReplayBatch) metricDrillJumpKeyRef.current = null;
  }, [compareReplayBatch]);

  useEffect(() => {
    const md = activeMetricDrill;
    if (!compareReplayBatch || !md?.highlights?.length) return;
    const firstTn = Math.min(...md.highlights.map(h => h.trickNumber));
    const sessionKey = `${compareReplayBatch.selectedIndex}|${md.metric}|${firstTn}`;
    if (metricDrillJumpKeyRef.current === sessionKey) return;
    const ct = gameState.completedTricks[firstTn - 1];
    if (!ct?.cards?.length) return;
    metricDrillJumpKeyRef.current = sessionKey;
    setReviewTrick({
      trickNumber: firstTn,
      cards: ct.cards,
      players: ct.players,
    });
    setReviewPhase(null);
  }, [
    activeMetricDrill,
    compareReplayBatch,
    gameState.completedTricks,
  ]);

  const handleDealSetupChange = useCallback((next: TrainingDealSetupValue) => {
    setDealSetup(next);
    saveTrainingDealSetup(session.pseudo, next);
  }, [session.pseudo]);

  const fetchMistakeExercises = useCallback(async (opts?: {
    offset?: number;
    append?: boolean;
    refresh?: boolean;
  }) => {
    const offset = opts?.offset ?? 0;
    const pseudo = session.pseudo;
    setMistakeLoading(true);
    setMistakeError(null);
    try {
      const q = new URLSearchParams({
        pseudo,
        offset: String(offset),
        limit: '10',
      });
      if (opts?.refresh) q.set('refresh', '1');
      const res = await fetch(`${BACKEND_URL}/api/training/exercises/mistakes?${q}`);
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || `Erreur ${res.status}`);
      }
      const list = (data.exercises ?? []) as MistakeExerciseCard[];
      setMistakeExercises((prev) => (opts?.append ? [...prev, ...list] : list));
      setMistakeHasMore(Boolean(data.hasMore));
      setMistakeOffset(Number(data.nextOffset) || offset + list.length);
      const emptyKind = data.emptyKind === 'clean_record' || data.emptyKind === 'no_recent_play'
        ? (data.emptyKind as MistakesEmptyKind)
        : null;
      setMistakeEmptyKind(emptyKind);
      setMistakeCoverage({
        scannedDeals: Number(data.scannedDeals) || 0,
        tournamentsConsidered: Number(data.tournamentsConsidered) || 0,
        days: Number(data.days) || 90,
      });
      setMistakeFetchedFor(pseudo);
    } catch (e) {
      setMistakeError(e instanceof Error ? e.message : 'Erreur réseau');
      if (!opts?.append) {
        setMistakeExercises([]);
        setMistakeEmptyKind(null);
        setMistakeCoverage(null);
      }
      setMistakeFetchedFor(pseudo);
    } finally {
      setMistakeLoading(false);
    }
  }, [session.pseudo]);

  useEffect(() => {
    if (isActive || homeTab !== 'exercices') return;
    if (mistakeLoading || mistakeFetchedFor === session.pseudo) return;
    void fetchMistakeExercises({ refresh: false });
  }, [isActive, homeTab, mistakeLoading, mistakeFetchedFor, session.pseudo, fetchMistakeExercises]);

  const handleOpenMistakeExercise = useCallback(async (ex: MistakeExerciseCard) => {
    setMistakeOpeningId(ex.id);
    setMistakeError(null);
    try {
      const q = new URLSearchParams({
        pseudo: session.pseudo,
        differeId: String(ex.differeId),
        resultId: String(ex.resultId),
        dealIndex: String(ex.dealIndex),
        bestResultId: String(ex.bestResultId),
      });
      const res = await fetch(`${BACKEND_URL}/api/training/exercises/mistakes/open?${q}`);
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || `Erreur ${res.status}`);
      }
      const loadRequest: TrainingHistoryLoadRequest = {
        gameData: data.gameData,
        mode: 'replay',
        position: 0,
        replayAllBots: true,
        replaySeatLabels: data.replaySeatLabels,
        compareReplayBatch: data.compareReplayBatch,
        bifurcationTricks: data.bifurcationTricks,
        historyContext: data.historyContext,
      };
      startTraining({
        sessionId: '',
        displayMode: 'cards_on_table',
        showTimeline: true,
        botConfig: { disabledBots: new Set() },
      });
      applyTrainingLoadRequest(loadRequest);
    } catch (e) {
      setMistakeError(e instanceof Error ? e.message : 'Impossible d\'ouvrir l\'exercice');
    } finally {
      setMistakeOpeningId(null);
    }
  }, [session.pseudo, applyTrainingLoadRequest, startTraining]);

  const dealSetupValidation = useMemo(
    () => validateHandQualities(dealSetup.handQualities),
    [dealSetup.handQualities],
  );

  const handleStartTraining = useCallback(() => {
    const socket = socketRef.current;
    if (!socket) return;
    if (!dealSetupValidation.valid) return;
    if (isActiveRef.current) {
      saveSessionRef.current('in_progress');
    }
    setCompareReplayBatch(null);
    setLibraryGameId(null);
    setLibraryGameSource('own');
    setLibraryCommentsEnabled(true);
    setLibraryReplaySeatLabelsFromPayload(false);
    setCommentingContext(null);
    libraryLoadInitialHandsRef.current = null;
    pendingReplayBidsRef.current = null;
    setBifurcationTricks(new Set());
    setExerciseMistakeMode(false);
    setShowReferencePane(false);
    saveTrainingDealSetup(session.pseudo, dealSetup);
    socket.emit('training:create', {
      pseudo: session.pseudo,
      disabledBots: Array.from(disabledBots),
      speed: gameSpeed,
      compet: session.compet ?? false,
      modelSelection: aiModelSelection,
      constraints: toTrainingDealConstraints(dealSetup) ?? {},
    });
    startTraining({
      sessionId: '',
      displayMode,
      showTimeline,
      botConfig: { disabledBots },
    });
  }, [
    session.pseudo,
    session.compet,
    disabledBots,
    gameSpeed,
    aiModelSelection,
    displayMode,
    showTimeline,
    startTraining,
    dealSetup,
    dealSetupValidation.valid,
  ]);

  const feedbackCardClickRef = useRef<((card: CardNumber) => void) | null>(null);

  const submitPollVote = useCallback(async (
    voteType: PollVoteType, cardPlayed: number, voteValue?: string, label?: string,
  ) => {
    if (!pollMode || pollHasVoted) return;
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/shared/${pollMode.sharedGameId}/poll?pseudo=${encodeURIComponent(session.pseudo)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ card_played: cardPlayed, vote_type: voteType, vote_value: voteValue }),
        },
      );
      if (res.ok) {
        const data = await res.json();
        setPollHasVoted(true);
        setPollAfterVote({
          voteType,
          voteLabel: label ?? String(cardPlayed),
          responses: data.responses ?? [],
        });
      }
    } catch { /* ignore */ }
  }, [pollMode, pollHasVoted, session.pseudo]);

  const handlePlayCard = useCallback((card: CardNumber, seat?: number) => {
    if (feedbackMode) {
      if (mlSuggestion && feedbackCardClickRef.current) {
        const feedbackSeat = mlSuggestion.seat;
        const clickedSeat = seat ?? 0;
        if (clickedSeat === feedbackSeat) {
          feedbackCardClickRef.current(card);
        }
      }
      return;
    }

    if (pollMode && !pollHasVoted) {
      submitPollVote('card', card, undefined, getCardName(card));
      return;
    }

    const socket = socketRef.current;
    if (!socket) return;
    socket.emit('training:play', { card, seat });
  }, [feedbackMode, mlSuggestion, pollMode, pollHasVoted, submitPollVote]);

  const handleRewindCard = useCallback((positionInTrick: number, trickNumber?: number) => {
    const socket = socketRef.current;
    if (!socket) return;
    socket.emit('training:rewind_trick', { positionInTrick, trickNumber });
  }, []);

  const handleCommentCard = useCallback((trickNumber: number, positionInTrick: number) => {
    if (!libraryGameId) return;
    setCommentingContext({
      type: 'card',
      trickNum: trickNumber,
      cardIndex: positionInTrick,
      label: `Carte ${positionInTrick + 1} du pli ${trickNumber}`,
    });
  }, [libraryGameId]);

  const handleCommentTrick = useCallback((trickNumber: number) => {
    if (!libraryGameId) return;
    setCommentingContext({
      type: 'trick',
      trickNum: trickNumber,
      label: `Pli ${trickNumber}`,
    });
  }, [libraryGameId]);

  const handleCommentBid = useCallback((bidIndex: number, player: number, bid: number, e: React.MouseEvent) => {
    void e;
    if (!libraryGameId) return;
    const playerName = gameState.players.find(p => p.position === player)?.pseudo ?? `J${player}`;
    const bidName = bid === 0 ? 'Passe' : bid === 1 ? 'Petite' : bid === 2 ? 'Garde' : bid === 3 ? 'G.sans' : bid === 4 ? 'G.contre' : String(bid);
    setCommentingContext({
      type: 'bid',
      bidIndex,
      trickNum: player,
      label: `Enchère ${playerName}: ${bidName}`,
    });
  }, [libraryGameId, gameState.players]);

  const handleBid = useCallback((bid: number) => {
    if (pollMode && !pollHasVoted) {
      const bidName = bid === 0 ? 'Passe' : bid === 1 ? 'Petite' : bid === 2 ? 'Garde' : bid === 3 ? 'Garde sans' : bid === 4 ? 'Garde contre' : String(bid);
      submitPollVote('bid', bid, undefined, bidName);
      return;
    }
    const socket = socketRef.current;
    if (!socket) return;
    const cp = gameState.currentPlayer;
    const seat = cp !== 0 && disabledBots.has(cp) ? cp : undefined;
    socket.emit('training:bid', { bid, seat });
  }, [gameState.currentPlayer, disabledBots, pollMode, pollHasVoted, submitPollVote]);

  const handleEcart = useCallback((cards: CardNumber[], seat?: number) => {
    const socket = socketRef.current;
    if (!socket) return;
    socket.emit('training:ecart', { cards, seat });
  }, []);

  const handleToggleEcartCard = useCallback((card: CardNumber) => {
    setEcartSelectedCards(prev =>
      prev.includes(card) ? prev.filter(c => c !== card) : [...prev, card],
    );
  }, []);

  const handleValidateEcart = useCallback(() => {
    if (ecartSelectedCards.length !== 6) return;
    const taker = gameState.taker;
    if (taker === null) return;

    if (pollMode && !pollHasVoted) {
      const label = ecartSelectedCards.map(c => getCardName(c)).join(', ');
      submitPollVote('ecart', ecartSelectedCards.length, ecartSelectedCards.join(','), label);
      return;
    }

    const seat = taker !== 0 && disabledBots.has(taker) ? taker : undefined;
    handleEcart(ecartSelectedCards, seat);
  }, [ecartSelectedCards, gameState.taker, disabledBots, handleEcart, pollMode, pollHasVoted, submitPollVote]);

  const handleDismissChien = useCallback(() => {
    setShowChienOverlay(false);
    const socket = socketRef.current;
    if (socket) socket.emit('training:chien_acknowledged');
  }, []);

  const handleToggleDisplayMode = useCallback(() => {
    setDisplayMode(prev => prev === 'classic' ? 'cards_on_table' : 'classic');
  }, []);

  const handleToggleTimeline = useCallback(() => {
    setShowTimeline(prev => !prev);
  }, []);

  const handleRotateBoard = useCallback(() => {
    setViewRotation(prev => (prev + 1) % 4);
  }, []);

  const viewPosition = (activePosition + viewRotation) % 4;

  const handleToggleBot = useCallback((seat: number) => {
    const socket = socketRef.current;
    if (!socket) return;

    if (seat === gameState.myPosition) {
      const newState = !humanAutomated;
      socket.emit('training:toggle_bot', { seat, enabled: newState });
      setHumanAutomated(newState);
      return;
    }

    const isCurrentlyDisabled = disabledBots.has(seat);
    socket.emit('training:toggle_bot', { seat, enabled: isCurrentlyDisabled });
  }, [disabledBots, humanAutomated, gameState.myPosition]);

  /** Pré-réglage bots avant création de session (pas d'event socket). */
  const handleToggleBotPref = useCallback((seat: number) => {
    setDisabledBots((prev) => {
      const next = new Set(prev);
      if (next.has(seat)) next.delete(seat);
      else next.add(seat);
      return next;
    });
  }, []);

  const handleNewDeal = useCallback((constraints?: TrainingDealConstraints) => {
    const socket = socketRef.current;
    if (!socket) return;
    saveSessionRef.current('finished');
    setCompareReplayBatch(null);
    saveTrainingDealSetup(session.pseudo, dealSetup);
    // Objet vide = reset aléatoire côté serveur (undefined laisserait l'ancienne contrainte).
    const nextConstraints = constraints ?? toTrainingDealConstraints(dealSetup) ?? {};
    socket.emit('training:new_deal', { constraints: nextConstraints });
  }, [session.pseudo, dealSetup]);

  const handleRestartSameDeal = useCallback(() => {
    setShowPositionPicker(true);
  }, []);

  const handleRestartWithPosition = useCallback((position: number) => {
    const socket = socketRef.current;
    if (!socket) return;
    saveSessionRef.current('finished');

    if (position !== activePosition) {
      for (const seat of disabledBots) {
        socket.emit('training:toggle_bot', { seat, enabled: true });
      }
      if (humanAutomated) {
        socket.emit('training:toggle_bot', { seat: 0, enabled: false });
      }

      if (position !== 0) {
        socket.emit('training:toggle_bot', { seat: position, enabled: false });
        socket.emit('training:toggle_bot', { seat: 0, enabled: true });
      }

      setDisabledBots(position !== 0 ? new Set([position]) : new Set());
      setHumanAutomated(position !== 0);
      setActivePosition(position);
    }

    socket.emit('training:new_deal', { sameDeal: true });
    setShowPositionPicker(false);
  }, [disabledBots, humanAutomated, activePosition]);

  const handleAbandon = useCallback(() => {
    const socket = socketRef.current;
    if (!socket) return;
    saveSessionRef.current('in_progress');
    socket.emit('training:abandon');
  }, []);

  const handleCloseToHome = useCallback(() => {
    handleAbandon();
    setIsActive(false);
  }, [handleAbandon]);
  useTabReselect('training', () => {
    setTrainingLeftColumnHidden(false);
    setHomeTab('libre');
    if (isActiveRef.current) handleCloseToHome();
  });

  const handleRebidAfterPass = useCallback(() => {
    const socket = socketRef.current;
    if (!socket) return;
    socket.emit('training:rebid_after_pass');
  }, []);

  const handleForceBid = useCallback((seat: number, contract: number) => {
    const socket = socketRef.current;
    if (!socket) return;
    socket.emit('training:force_bid', { seat, contract });
  }, []);

  const handleReady = useCallback(() => {
    const socket = socketRef.current;
    if (!socket) return;
    socket.emit('training:ready');
  }, []);

  const buildTrainingGameHistory = useCallback((includeCurrentTrick = false): GameHistory | null => {
    if (!gameState.players.length) return null;
    if (!includeCurrentTrick && gameState.completedTricks.length === 0) return null;

    const initialHands = new Map<number, CardNumber[]>();
    for (const [pos, hand] of Object.entries(initialHandsRef.current)) {
      initialHands.set(Number(pos), [...hand]);
    }

    const tricks = gameState.completedTricks.map((t, i) => ({
      trickNumber: i + 1,
      leader: t.players[0],
      cards: t.cards.map((card, ci) => ({
        player: t.players[ci],
        card,
        timestamp: Date.now(),
      })),
      winner: t.winner ?? t.players[0],
    }));

    if (includeCurrentTrick && gameState.currentTrick.length > 0) {
      tricks.push({
        trickNumber: gameState.completedTricks.length + 1,
        leader: gameState.trickPlayerPositions[0] ?? 0,
        cards: gameState.currentTrick.map((card, ci) => ({
          player: gameState.trickPlayerPositions[ci] ?? 0,
          card,
          timestamp: Date.now(),
        })),
        winner: gameState.trickPlayerPositions[0] ?? 0,
      });
    }

    return {
      players: gameState.players.map(p => ({
        name: p.pseudo,
        position: p.position,
        isConnected: p.isConnected,
      })),
      playerCount: gameState.players.length || 4,
      dealer: gameState.dealer,
      taker: gameState.taker ?? 0,
      contract: (gameState.highestBid ?? 0) as Contract,
      myPosition: 0,
      initialHands,
      chien: gameState.chien ? [...gameState.chien] : [],
      ecart: [...ecartCards],
      bids: gameState.bids.map(b => ({ player: b.player, bid: b.bid as Contract, timestamp: Date.now() })),
      tricks,
      poignees: [],
      score: gameState.scores ?? undefined,
      steps: [],
    };
  }, [gameState, ecartCards]);

  // Timeline data: completed tricks show immediately
  const timelineTricks = useMemo(() => {
    return gameState.completedTricks.map((trick, i) => ({
      trickNumber: i + 1,
      cards: trick.cards.map((card, ci) => ({
        card,
        player: trick.players[ci],
      })),
      winner: trick.winner,
    }));
  }, [gameState.completedTricks]);

  const handleTrickClick = useCallback((trickNumber: number) => {
    const trick = gameState.completedTricks[trickNumber - 1];
    if (trick) {
      setReviewTrick({ cards: trick.cards, players: trick.players, trickNumber });
      setReviewPhase(null);
      if (bifurcationTricks.has(trickNumber)) {
        setShowReferencePane(true);
        setRevealReference(true);
      }
    }
  }, [gameState.completedTricks, bifurcationTricks]);

  const handleCurrentTrickClick = useCallback(() => {
    setReviewTrick(null);
    setReviewPhase(null);
  }, []);

  const currentTrickTimelineCards = useMemo(() => {
    return gameState.currentTrick.map((card, i) => ({
      card,
      player: gameState.trickPlayerPositions[i],
    }));
  }, [gameState.currentTrick, gameState.trickPlayerPositions]);

  const handleAddCoachMessage = useCallback((msg: Omit<CoachMessage, 'id' | 'timestamp'>) => {
    const newMsg: CoachMessage = {
      ...msg,
      id: `coach-${Date.now()}-${++coachIdCounter.current}`,
      timestamp: Date.now(),
    };
    setCoachMessages(prev => [...prev, newMsg]);

    if (msg.type === 'advice' && msg.card != null) {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      setHighlightedCard(msg.card);
      highlightTimerRef.current = setTimeout(() => {
        setHighlightedCard(null);
        highlightTimerRef.current = null;
      }, 3000);
    }
  }, []);

  const isBidding = gameState.phase === 'bidding';
  const isEcart = gameState.phase === 'ecart';
  const isEndOfDeal = gameState.phase === 'end_of_deal';
  const isPlaying = gameState.phase === 'playing';

  // ==========================================
  // Table de référence (donnes duplicate / exercices bif)
  // ==========================================
  /** Batch enrichi (noms d'équipes) même pour un historique chargé avant ces champs. */
  const compareReplayBatchEnriched = useMemo(
    () => (compareReplayBatch
      ? enrichCompareReplayBatchWithTeams(compareReplayBatch, historyContextRef.current?.teamChamp)
      : null),
    [compareReplayBatch],
  );

  const currentCompareDeal = compareReplayBatchEnriched?.deals[compareReplayBatchEnriched.selectedIndex] ?? null;
  const linkedDualTables = (currentCompareDeal?.variants?.length ?? 0) >= 2;

  /** Variante affichée à gauche (= colonne 0 du tableau en dual lié). */
  const leftBoardVariant = useMemo(() => {
    if (!currentCompareDeal?.variants?.length) return null;
    if (showReferencePane && linkedDualTables) {
      return currentCompareDeal.variants[0] ?? null;
    }
    return currentCompareDeal.variants[compareReplayBatchEnriched!.selectedVariantIndex] ?? null;
  }, [currentCompareDeal, showReferencePane, linkedDualTables, compareReplayBatchEnriched]);

  const referenceVariant = useMemo(() => {
    if (!currentCompareDeal?.variants || currentCompareDeal.variants.length < 2) return null;
    // Dual lié : droite = colonne 1 du tableau des scores (ordre fixe).
    if (showReferencePane && linkedDualTables) {
      return currentCompareDeal.variants[1] ?? null;
    }
    const otherIndex = currentCompareDeal.variants.findIndex(
      (_, i) => i !== (compareReplayBatchEnriched?.selectedVariantIndex ?? 0),
    );
    return otherIndex >= 0 ? currentCompareDeal.variants[otherIndex] : null;
  }, [currentCompareDeal, showReferencePane, linkedDualTables, compareReplayBatchEnriched?.selectedVariantIndex]);

  const referenceHistory = useMemo(() => {
    if (!referenceVariant) return null;
    return buildGameHistoryFromTrainingGameData(
      referenceVariant.gameData,
      referenceVariant.replaySeatLabels,
      viewPosition,
    );
  }, [referenceVariant, viewPosition]);

  // Le dévoilement est explicite et ne survit pas au changement de donne
  useEffect(() => {
    setRevealReference(false);
  }, [compareReplayBatch?.selectedIndex, compareReplayBatch?.selectedVariantIndex]);

  // Dual lié : forcer la colonne gauche (= variants[0]) dès l'activation du mode deux tables.
  useEffect(() => {
    if (!showReferencePane || !compareReplayBatch || !linkedDualTables) return;
    if (compareReplayBatch.selectedVariantIndex === 0) return;
    handleSelectCompareReplayDeal(compareReplayBatch.selectedIndex, 0);
  }, [
    showReferencePane,
    linkedDualTables,
    compareReplayBatch,
    handleSelectCompareReplayDeal,
  ]);

  const referenceCursor = useMemo<ReplayCursor>(() => {
    if (reviewTrick) {
      return {
        kind: 'trick',
        trickNumber: reviewTrick.trickNumber,
        cardIndex: reviewAnimIndex,
      };
    }
    if (reviewPhase?.type === 'bids') return { kind: 'bids' };
    if (reviewPhase?.type === 'chien') return { kind: 'chien' };
    if (reviewPhase?.type === 'ecart') return { kind: 'ecart' };
    return cursorFromLiveProgress(
      gameState.completedTricks.length,
      gameState.currentTrick.length,
    );
  }, [
    reviewTrick,
    reviewPhase,
    reviewAnimIndex,
    gameState.completedTricks.length,
    gameState.currentTrick.length,
  ]);

  const referencePosition = useMemo(
    () => (referenceHistory ? resolveCursor(referenceHistory, referenceCursor) : null),
    [referenceHistory, referenceCursor],
  );

  const referencePaneVisible = Boolean(showReferencePane && referenceHistory && referencePosition);

  const leftTeamHeader = leftBoardVariant?.attackTeam && leftBoardVariant?.defenseTeam
    ? leftBoardVariant
    : null;
  const rightTeamHeader = referenceVariant?.attackTeam && referenceVariant?.defenseTeam
    ? referenceVariant
    : null;

  // Dynamic player display names:
  // - Before taker is known: original pseudos ("Robot (Est)" etc.)
  // - After taker is known: attacker model for taker, defender model for others, with (position) suffix
  // - If human is automated: show the corresponding model name
  // - If human is not automated: show their real pseudo
  const SEAT_NAMES: Record<number, string> = { 0: 'Sud', 1: 'Est', 2: 'Nord', 3: 'Ouest' };
  const displayPlayers = useMemo(() => {
    if (libraryReplaySeatLabelsFromPayload) {
      return gameState.players.map(p => ({ ...p }));
    }
    return gameState.players.map(p => {
      if (p.position === gameState.myPosition && !humanAutomated) {
        return p;
      }
      if (p.position === gameState.myPosition && humanAutomated) {
        const role = gameState.taker === p.position ? 'attacker' : 'defender';
        return { ...p, pseudo: `${modelNames[role]} (${SEAT_NAMES[p.position]})` };
      }
      if (!p.isBot) return p;
      if (gameState.taker === null) return p;
      const role = gameState.taker === p.position ? 'attacker' : 'defender';
      return { ...p, pseudo: `${modelNames[role]} (${SEAT_NAMES[p.position]})` };
    });
  }, [gameState.players, gameState.taker, gameState.myPosition, humanAutomated, modelNames, libraryReplaySeatLabelsFromPayload]);

  const humanControlledPositions = useMemo(() => {
    const positions = new Set<number>();
    if (libraryReplaySeatLabelsFromPayload) return positions;
    if (!humanAutomated) positions.add(gameState.myPosition);
    for (const seat of disabledBots) positions.add(seat);
    return positions;
  }, [humanAutomated, disabledBots, libraryReplaySeatLabelsFromPayload, gameState.myPosition]);

  const isMyTurn = humanControlledPositions.has(gameState.currentPlayer);

  const maxBidAllowed = useMemo(() => {
    const forcedValues = Object.entries(forcedBids)
      .filter(([seat]) => Number(seat) !== gameState.currentPlayer)
      .map(([, contract]) => contract);
    if (forcedValues.length === 0) return 4;
    return Math.max(...forcedValues) - 1;
  }, [forcedBids, gameState.currentPlayer]);

  const bidderName = useMemo(() => {
    const p = displayPlayers.find(pl => pl.position === gameState.currentPlayer);
    return p?.pseudo ?? `Joueur ${gameState.currentPlayer}`;
  }, [displayPlayers, gameState.currentPlayer]);

  const saveCurrentSessionToHistory = useCallback((status: TrainingHistoryStatus) => {
    if (!gameState.players.length || gameState.phase === 'waiting') return;

    const history = buildTrainingGameHistory(true);
    if (!history) return;

    const gameData = gameHistoryToTrainingGameData(history);
    const isFinished = status === 'finished' || gameState.phase === 'end_of_deal';
    const isPartial = !isFinished;
    const partialPhaseNum = isPartial
      ? (gameState.phase === 'bidding' ? 1 : gameState.phase === 'ecart' ? 2 : 3)
      : undefined;
    const partialTrick = isPartial && (gameState.phase === 'playing' || gameState.phase === 'end_of_deal')
      ? gameState.completedTricks.length + (gameState.currentTrick.length > 0 ? 1 : 0)
      : isPartial && gameState.phase !== 'bidding' && gameState.phase !== 'ecart'
        ? gameState.completedTricks.length
        : undefined;

    const replaySeatLabels = gameState.players.map((p) => p.pseudo);
    const batchSnapshot = compareReplayBatch
      ? snapshotCompareReplayBatchForHistory(compareReplayBatch, gameData, replaySeatLabels)
      : undefined;

    const mode = trainingSessionModeRef.current;
    const replayAllBots = mode === 'replay' && (humanAutomated || libraryReplaySeatLabelsFromPayload);

    const context = enrichHistoryContext(historyContextRef.current, {
      compareReplayBatch: compareReplayBatch
        ? {
            deals: compareReplayBatch.deals,
            selectedIndex: compareReplayBatch.selectedIndex,
            selectedVariantIndex: compareReplayBatch.selectedVariantIndex,
          }
        : null,
      pollMode,
      libraryGameId,
      libraryGameSource,
    });

    const source: TrainingHistorySource = context?.kind ?? (
      compareReplayBatch ? 'compare'
        : pollMode ? 'poll'
          : libraryGameId ? 'library'
            : 'solo'
    );

    const position = humanAutomated
      ? 0
      : (disabledBots.size === 1 ? [...disabledBots][0] : activePosition);

    const loadRequest: TrainingHistoryLoadRequest = {
      libraryGameId,
      libraryGameSource,
      commentsEnabled: libraryCommentsEnabled,
      gameData,
      mode,
      position,
      biddingMode: mode === 'play' ? 'auto' : undefined,
      isPartial: isPartial || undefined,
      partialPhase: partialPhaseNum,
      partialTrickNum: partialTrick,
      replayAllBots: replayAllBots || undefined,
      replaySeatLabels: replayAllBots && replaySeatLabels.length === 4
        ? replaySeatLabels
        : undefined,
      pollVoteMode: pollMode ? true : undefined,
      compareReplayBatch: batchSnapshot,
      historyContext: context,
    };

    const id = currentHistoryEntryIdRef.current ?? createTrainingHistoryId();
    const createdAt = currentHistoryCreatedAtRef.current ?? Date.now();

    const entry: TrainingHistoryEntry = {
      id,
      createdAt,
      updatedAt: Date.now(),
      status: isFinished ? 'finished' : status,
      title: buildTrainingHistoryTitle({
        status: isFinished ? 'finished' : status,
        source,
        phase: gameState.phase,
        highestBid: gameState.highestBid,
        scores: gameState.scores,
        compareReplayBatch: compareReplayBatch
          ? {
              title: compareReplayBatch.title,
              deals: compareReplayBatch.deals,
              selectedIndex: compareReplayBatch.selectedIndex,
              selectedVariantIndex: compareReplayBatch.selectedVariantIndex,
            }
          : null,
      }),
      source,
      context,
      loadRequest,
      settings: {
        displayMode,
        showTimeline,
        disabledBots: Array.from(disabledBots),
        humanAutomated,
        gameSpeed,
        aiModelSelection,
        activePosition,
      },
      pollMode: pollMode ?? undefined,
    };

    upsertTrainingHistoryEntry(session.pseudo, entry);
    currentHistoryEntryIdRef.current = id;
    currentHistoryCreatedAtRef.current = createdAt;
    if (context) historyContextRef.current = context;
    if (!isActiveRef.current) {
      refreshHistoryList();
    }
  }, [
    activePosition,
    aiModelSelection,
    buildTrainingGameHistory,
    compareReplayBatch,
    disabledBots,
    displayMode,
    gameSpeed,
    gameState,
    humanAutomated,
    libraryCommentsEnabled,
    libraryGameId,
    libraryGameSource,
    libraryReplaySeatLabelsFromPayload,
    pollMode,
    refreshHistoryList,
    session.pseudo,
    showTimeline,
  ]);

  saveSessionRef.current = saveCurrentSessionToHistory;

  useEffect(() => {
    if (!isActive || gameState.phase === 'waiting') return;
    const timer = setTimeout(() => {
      saveCurrentSessionToHistory(
        gameState.phase === 'end_of_deal' ? 'finished' : 'in_progress',
      );
    }, 4000);
    return () => clearTimeout(timer);
  }, [
    isActive,
    gameState.phase,
    gameState.completedTricks.length,
    gameState.currentTrick.length,
    gameState.highestBid,
    gameState.dealNumber,
    compareReplayBatch?.selectedIndex,
    compareReplayBatch?.selectedVariantIndex,
    saveCurrentSessionToHistory,
  ]);

  const handleResumeFromHistory = useCallback(async (entry: TrainingHistoryEntry) => {
    if (historyResumeLoading) return;

    if (isActiveRef.current) {
      saveCurrentSessionToHistory('in_progress');
    }

    currentHistoryEntryIdRef.current = entry.id;
    currentHistoryCreatedAtRef.current = entry.createdAt;
    historyContextRef.current = entry.context ?? entry.loadRequest.historyContext;

    if (entry.settings) {
      setDisplayMode(entry.settings.displayMode);
      setShowTimeline(entry.settings.showTimeline);
      setDisabledBots(new Set(entry.settings.disabledBots));
      setHumanAutomated(entry.settings.humanAutomated);
      setGameSpeed(entry.settings.gameSpeed);
      setAIModelSelection(entry.settings.aiModelSelection);
      setActivePosition(entry.settings.activePosition);
    }

    if (entry.pollMode) {
      setPollMode(entry.pollMode);
      setPollHasVoted(false);
      setPollAfterVote(null);
    } else {
      setPollMode(null);
    }

    let loadRequest = entry.loadRequest;

    if (differeCompareReplayBatchNeedsRebuild(loadRequest, entry.context)) {
      const ctx = entry.context ?? loadRequest.historyContext;
      const differe = ctx?.differe;
      if (differe?.differeId != null && differe.differeType && differe.pseudo) {
        setHistoryResumeLoading(true);
        try {
          const playerLabel = differe.pseudoDisplay || differe.pseudo;
          const batch = await buildDiffereCompareReplayBatch({
            diffType: differe.differeType,
            differeId: Number(differe.differeId),
            playerPseudo: differe.pseudo,
            playerLabel,
            typeLabel: resolveDiffereBatchTitle(
              differe.differeType,
              differe.subType,
              playerLabel,
            ),
            selectedDealNum: differe.dealNum,
            selectedPartieId: differe.partieId,
          });
          const selectedDeal = batch.deals[batch.selectedIndex] ?? batch.deals[0];
          const historyContext: TrainingHistoryContext = {
            kind: 'differe',
            differe: {
              ...differe,
              dealNum: selectedDeal?.dealIndex ?? differe.dealNum,
              partieId: selectedDeal?.partieId ?? differe.partieId,
            },
          };
          loadRequest = {
            ...loadRequest,
            compareReplayBatch: batch,
            replayAllBots: loadRequest.replayAllBots ?? true,
            historyContext,
          };
          historyContextRef.current = historyContext;
        } catch (e) {
          console.error('Différé batch rebuild failed:', e);
        } finally {
          setHistoryResumeLoading(false);
        }
      }
    }

    applyTrainingLoadRequest(loadRequest, { preserveHistoryEntry: true });
    startTraining({
      sessionId: '',
      displayMode: entry.settings?.displayMode ?? 'classic',
      showTimeline: entry.settings?.showTimeline ?? true,
      botConfig: { disabledBots: new Set(entry.settings?.disabledBots ?? []) },
    });
  }, [
    applyTrainingLoadRequest,
    historyResumeLoading,
    saveCurrentSessionToHistory,
    startTraining,
  ]);

  const handleDeleteHistoryEntry = useCallback((id: string) => {
    deleteTrainingHistoryEntry(session.pseudo, id);
    refreshHistoryList();
  }, [refreshHistoryList, session.pseudo]);

  const handleOpenDiffereSource = useCallback((entry: TrainingHistoryEntry) => {
    const req = buildDiffereNavigationRequest(entry);
    if (!req) return;
    navigateToDiffere(req);
  }, [navigateToDiffere]);

  const takerIsHumanControlled = gameState.taker !== null && humanControlledPositions.has(gameState.taker);

  const discardableCards = useMemo(() => {
    if (!isEcart || gameState.taker === null) return new Set<CardNumber>();
    const hand = gameState.allHands[gameState.taker] ?? [];
    const normallyDiscardable = hand.filter(c => c <= 56 && c % 14 !== 0);
    if (normallyDiscardable.length >= 6) return new Set(normallyDiscardable);
    const extended = hand.filter(c => {
      if (c <= 56 && c % 14 !== 0) return true;
      if (c >= 58 && c <= 76) return true;
      return false;
    });
    return new Set(extended);
  }, [isEcart, gameState.taker, gameState.allHands]);

  const canAskAdvice = isPlaying && isMyTurn && !lastCompletedTrick && !reviewTrick;
  const canShowFeedback = preferences.feedbackLevel !== 'none';

  const mlSuggestedCards = useMemo(() => {
    if (!feedbackMode || !mlSuggestion) return undefined;
    const cards = new Map<CardNumber, number>();
    cards.set(mlSuggestion.card as CardNumber, mlSuggestion.confidence);
    if (mlSuggestion.alternatives) {
      for (const alt of mlSuggestion.alternatives) {
        cards.set(alt.card as CardNumber, alt.confidence);
      }
    }
    return cards;
  }, [feedbackMode, mlSuggestion]);

  const handleToggleFeedbackMode = useCallback((enabled: boolean) => {
    setFeedbackMode(enabled);
    const socket = socketRef.current;
    if (socket) {
      socket.emit('training:set_feedback_mode', {
        enabled,
        level: preferences.feedbackLevel,
      });
    }
  }, [preferences.feedbackLevel]);

  const handleToggleFeedbackSeat = useCallback((seat: number) => {
    setFeedbackEnabledSeats(prev => {
      const next = new Set(prev);
      if (next.has(seat)) next.delete(seat); else next.add(seat);
      const socket = socketRef.current;
      if (socket && feedbackMode) {
        socket.emit('training:set_feedback_mode', {
          enabled: feedbackMode && next.size > 0,
          level: preferences.feedbackLevel,
          seats: [...next],
        });
      }
      return next;
    });
  }, [feedbackMode, preferences.feedbackLevel]);

  // Sync model selection to backend when preference or admin selector changes
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !gameState.sessionId) return;
    socket.emit('training:set_model_selection', aiModelSelection);
  }, [aiModelSelection, gameState.sessionId]);

  // Reset botModelSlot when useBetaModel pref changes
  useEffect(() => {
    const slot = preferences.useBetaModel ? 'beta' : 'production';
    setBotModelSlot(slot);
    setAIModelSelection({ attacker: slot, defender: slot, bidding: slot, discard: slot });
  }, [preferences.useBetaModel]);

  const handleToggleBotModelSlot = useCallback((slot: 'production' | 'beta') => {
    setBotModelSlot(slot);
    setAIModelSelection({ attacker: slot, defender: slot, bidding: slot, discard: slot });
  }, []);

  const activeModelSlot = mlSuggestion?.model_slot ?? aiModelSelection.defender;

  const handleSubmitFeedback = useCallback((data: FeedbackSubmitData) => {
    const socket = socketRef.current;
    if (!socket) return;
    socket.emit('training:submit_feedback', data);
    setMlSuggestion(null);
  }, []);

  const handleSpeedChange = useCallback((speed: GameSpeed) => {
    setGameSpeed(speed);
    const socket = socketRef.current;
    if (socket) socket.emit('training:set_speed', { speed });
  }, []);

  const handlePhaseReview = useCallback((type: 'chien' | 'ecart' | 'bids') => {
    if (type === 'bids') {
      if (!gameState.bids || gameState.bids.length === 0) return;
      setReviewPhase({ type: 'bids', bids: gameState.bids });
    } else {
      const cards = type === 'chien' ? gameState.chien : ecartCards;
      if (!cards || cards.length === 0) return;
      setReviewPhase({ type, cards });
    }
    setReviewTrick(null);
  }, [gameState.chien, ecartCards, gameState.bids]);

  const handleReviewPrev = useCallback(() => {
    const total = gameState.completedTricks.length;
    if (total === 0) return;
    if (!reviewTrick) {
      handleTrickClick(total);
    } else if (reviewTrick.trickNumber > 1) {
      handleTrickClick(reviewTrick.trickNumber - 1);
    }
  }, [gameState.completedTricks.length, reviewTrick, handleTrickClick]);

  const handleReviewNext = useCallback(() => {
    const total = gameState.completedTricks.length;
    if (!reviewTrick || reviewTrick.trickNumber >= total) {
      setReviewTrick(null);
    } else {
      handleTrickClick(reviewTrick.trickNumber + 1);
    }
  }, [reviewTrick, gameState.completedTricks.length, handleTrickClick]);

  /** Avant le premier paint : évite reviewAnimIndex === 0 (aucune carte au centre, mains incohérentes) */
  useLayoutEffect(() => {
    if (!reviewTrick) {
      setReviewAnimIndex(0);
      return;
    }
    setReviewAnimIndex(1);
  }, [reviewTrick]);

  useEffect(() => {
    if (!reviewTrick) return;
    if (gameSpeed === 'click') return;
    const totalCards = reviewTrick.cards.length;
    if (totalCards <= 1) return;
    const delayMs = gameSpeed === 'fast' ? 250 : gameSpeed === 'normal' ? 400 : 600;
    let count = 1;
    const timer = setInterval(() => {
      count++;
      setReviewAnimIndex(count);
      if (count >= totalCards) clearInterval(timer);
    }, delayMs);
    return () => clearInterval(timer);
  }, [reviewTrick, gameSpeed]);

  const reviewHands = useMemo(() => {
    if (!reviewTrick && !reviewPhase) return null;

    const initial = initialHandsRef.current;
    const taker = gameState.taker ?? 0;
    const contract = gameState.highestBid ?? Contract.Petite;
    const chienCards = gameState.chien ?? [];
    const result: Record<number, CardNumber[]> = {};
    for (let pos = 0; pos < 4; pos++) {
      result[pos] = [...(initial[pos] ?? [])];
    }

    if (reviewPhase?.type === 'bids') {
      return result;
    }

    if (reviewPhase?.type === 'chien') {
      if (contract <= Contract.Garde) {
        result[taker] = [...result[taker], ...chienCards];
      }
      return result;
    }

    if (reviewPhase?.type === 'ecart') {
      if (contract <= Contract.Garde) {
        result[taker] = [...result[taker], ...chienCards];
      }
      return result;
    }

    // Revue d'un pli : partir des mains post-écart, retirer les plis passés
    // puis les cartes déjà affichées du pli courant (pas de double comptage).
    if (contract <= Contract.Garde) {
      result[taker] = [...result[taker], ...chienCards];
      for (const c of ecartCards) {
        const idx = result[taker].indexOf(c);
        if (idx !== -1) result[taker].splice(idx, 1);
      }
    }

    if (!reviewTrick) return result;

    const trickNum = reviewTrick.trickNumber;
    for (let t = 0; t < gameState.completedTricks.length; t++) {
      if (t + 1 >= trickNum) break;
      const trick = gameState.completedTricks[t];
      for (let c = 0; c < trick.cards.length; c++) {
        const pos = trick.players[c];
        const hand = result[pos];
        if (!hand) continue;
        const idx = hand.indexOf(trick.cards[c]);
        if (idx !== -1) hand.splice(idx, 1);
      }
    }

    const cardsShown = Math.min(reviewAnimIndex, reviewTrick.cards.length);
    for (let i = 0; i < cardsShown; i++) {
      const pos = reviewTrick.players[i];
      const hand = result[pos];
      if (!hand) continue;
      const idx = hand.indexOf(reviewTrick.cards[i]);
      if (idx !== -1) hand.splice(idx, 1);
    }
    return result;
  }, [
    reviewTrick,
    reviewPhase,
    reviewAnimIndex,
    gameState.completedTricks,
    gameState.taker,
    gameState.highestBid,
    gameState.chien,
    ecartCards,
  ]);

  const handleBoardClick = useCallback(() => {
    if (gameSpeed !== 'click') return;
    if (reviewTrick && reviewAnimIndex < reviewTrick.cards.length) {
      setReviewAnimIndex(prev => prev + 1);
      return;
    }
    if (lastCompletedTrick) {
      setLastCompletedTrick(null);
      return;
    }
    const socket = socketRef.current;
    if (socket) socket.emit('training:advance');
    setAwaitingAdvance(false);
  }, [gameSpeed, reviewTrick, reviewAnimIndex, lastCompletedTrick]);

  // ============================================
  // Start screen
  // ============================================

  if (!isActive) {
    const inProgressCount = countTrainingHistory(session.pseudo, 'in_progress');
    return (
      <div className="training-layout">
        <div className="training-start-screen" style={{ width: '100%' }}>
          <div className="training-start-hero">
            <h2>Mode Entraînement</h2>
            <p>
              Entraînement libre, exercices issus de vos différés,
              ou reprise depuis l&apos;historique.
            </p>
          </div>

          <div
            className="training-home-tabs"
            role="tablist"
            aria-label="Sections entraînement"
          >
            {([
              ['libre', 'Libre'],
              ['exercices', 'Exercices'],
              ['historique', 'Historique'],
            ] as const).map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={homeTab === id}
                className={`training-home-tab ${homeTab === id ? 'active' : ''}`}
                onClick={() => setHomeTab(id)}
              >
                {label}
              </button>
            ))}
          </div>

          {homeTab === 'libre' && (
            <>
              <TrainingDealSetup
                variant="home"
                value={dealSetup}
                onChange={handleDealSetupChange}
                gameSpeed={gameSpeed}
                onSpeedChange={setGameSpeed}
                disabledBots={disabledBots}
                onToggleBot={handleToggleBotPref}
                humanAutomated={humanAutomated}
                aiModelSelection={aiModelSelection}
                onAIModelSelectionChange={setAIModelSelection}
                isAdmin={isAdmin}
              />

              {!dealSetupValidation.valid && (
                <div className="training-constraint-error" style={{ maxWidth: 520 }}>
                  {dealSetupValidation.error}
                </div>
              )}

              <button
                type="button"
                className="training-start-btn"
                onClick={handleStartTraining}
                disabled={!dealSetupValidation.valid}
              >
                Lancer l&apos;entraînement
              </button>
            </>
          )}

          {homeTab === 'exercices' && (
            <div className="training-exercises-section">
              <div className="training-exercises-header">
                <h3>Tes erreurs (différé)</h3>
                <p>
                  Donnes où une autre ligne de jeu aurait amélioré votre score.
                  Cliquez pour comparer votre jouerie à la meilleure bifurcation.
                </p>
                <button
                  type="button"
                  className="training-btn training-btn-secondary"
                  disabled={mistakeLoading}
                  onClick={() => void fetchMistakeExercises({ offset: 0, refresh: true })}
                >
                  Actualiser
                </button>
              </div>

              {mistakeLoading && mistakeExercises.length === 0 && (
                <p className="training-history-loading">
                  Analyse de vos différés en cours… (peut prendre une minute)
                </p>
              )}
              {mistakeError && (
                <p className="training-constraint-error">{mistakeError}</p>
              )}
              {!mistakeLoading && !mistakeError && mistakeExercises.length === 0 && (
                <p className="training-history-empty">
                  {mistakeEmptyKind === 'clean_record' ? (
                    <>
                      Bravo — aucune bifurcation coûteuse sur vos différés récents
                      {mistakeCoverage && mistakeCoverage.scannedDeals > 0
                        ? ` (${mistakeCoverage.scannedDeals} donne${mistakeCoverage.scannedDeals > 1 ? 's' : ''} analysée${mistakeCoverage.scannedDeals > 1 ? 's' : ''})`
                        : ''}
                      . Belle jouerie !
                    </>
                  ) : (
                    <>
                      Pas assez de donnes de différé individuel récentes pour dresser un bilan
                      {mistakeCoverage ? ` (fenêtre ${mistakeCoverage.days} jours)` : ''}
                      .
                      Jouez davantage au différé : vos erreurs coûteuses apparaîtront ici pour vous entraîner.
                    </>
                  )}
                </p>
              )}

              {mistakeExercises.length > 0 && (
                <ul className="training-exercises-list">
                  {mistakeExercises.map((ex) => (
                    <li key={ex.id}>
                      <button
                        type="button"
                        className="training-exercise-card"
                        disabled={mistakeOpeningId === ex.id}
                        onClick={() => void handleOpenMistakeExercise(ex)}
                      >
                        <span className="training-exercise-card-top">
                          <span className="training-exercise-delta">+{ex.scoreDelta} pts</span>
                          <span className="training-exercise-contract">{ex.contractLabel}</span>
                        </span>
                        <span className="training-exercise-title">
                          Différé #{ex.differeId} — donne {ex.dealNum}
                        </span>
                        <span className="training-exercise-meta">
                          Pli {ex.trickIndex + 1}
                          {ex.effectTrickIndex != null && ex.effectTrickIndex !== ex.trickIndex
                            ? ` → effet pli ${ex.effectTrickIndex + 1}`
                            : ''}
                          {' · '}
                          vs {ex.bestPseudo}
                          {ex.dealScore != null ? ` · vous ${ex.dealScore}` : ''}
                          {` · meilleur ${ex.bestScore}`}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {mistakeHasMore && (
                <button
                  type="button"
                  className="training-start-btn"
                  style={{ marginTop: 12 }}
                  disabled={mistakeLoading}
                  onClick={() => void fetchMistakeExercises({
                    offset: mistakeOffset,
                    append: true,
                  })}
                >
                  {mistakeLoading ? 'Chargement…' : 'Autres donnes'}
                </button>
              )}
            </div>
          )}

          {homeTab === 'historique' && (
            <div className="training-history-section">
              <div className="training-history-header">
                <h3>
                  {historyFilter === 'finished'
                    ? `Parties terminées${finishedHistoryCount > 0 ? ` (${finishedHistoryCount})` : ''}`
                    : `Parties en cours${inProgressCount > 0 ? ` (${inProgressCount})` : ''}`}
                </h3>
                <div
                  className="training-history-switch"
                  role="group"
                  aria-label="Filtrer l'historique"
                >
                  <button
                    type="button"
                    className={`training-history-switch-btn ${historyFilter === 'in_progress' ? 'active' : ''}`}
                    onClick={() => setHistoryFilter('in_progress')}
                  >
                    En cours
                  </button>
                  <button
                    type="button"
                    className={`training-history-switch-btn ${historyFilter === 'finished' ? 'active' : ''}`}
                    onClick={() => setHistoryFilter('finished')}
                  >
                    Terminées
                  </button>
                </div>
              </div>

              {historyResumeLoading && (
                <p className="training-history-loading">Chargement des donnes différé…</p>
              )}

              {historyEntries.length === 0 ? (
                <p className="training-history-empty">
                  Aucune partie {historyFilter === 'finished' ? 'terminée' : 'en cours'} pour le moment.
                </p>
              ) : (
                <ul className="training-history-list">
                  {historyEntries.map((entry) => {
                    const display = formatTrainingHistoryDisplay(entry);
                    const showDiffereLink = canOpenDiffereSource(entry);
                    return (
                      <li key={entry.id} className="training-history-item">
                        <button
                          type="button"
                          className="training-history-item-main"
                          disabled={historyResumeLoading}
                          onClick={() => void handleResumeFromHistory(entry)}
                        >
                          <span className="training-history-item-top">
                            {display.sourceTag && (
                              <span className="training-history-source-tag">{display.sourceTag}</span>
                            )}
                            {display.teams && display.teams.length >= 2 ? (
                              <span className="training-history-item-title training-history-item-title--teams">
                                <span className="training-history-team-chip">
                                  <PlayerAvatar
                                    pseudo={display.teams[0].nom}
                                    size={20}
                                    photoUrl={`${BACKEND_URL}/api/avatars/team/${encodeURIComponent(display.teams[0].nom)}?size=40`}
                                  />
                                  <span className="training-history-team-name">{display.teams[0].nom}</span>
                                </span>
                                <span className="training-history-team-vs">vs</span>
                                <span className="training-history-team-chip">
                                  <PlayerAvatar
                                    pseudo={display.teams[1].nom}
                                    size={20}
                                    photoUrl={`${BACKEND_URL}/api/avatars/team/${encodeURIComponent(display.teams[1].nom)}?size=40`}
                                  />
                                  <span className="training-history-team-name">{display.teams[1].nom}</span>
                                </span>
                              </span>
                            ) : (
                              <span className="training-history-item-title">{display.title}</span>
                            )}
                          </span>
                          <span className="training-history-item-subtitle">{display.subtitle}</span>
                          <span className="training-history-item-date">
                            {display.matchDateLabel
                              ? `Match du ${display.matchDateLabel}`
                              : formatTrainingHistoryDate(entry.updatedAt)}
                          </span>
                        </button>
                        {showDiffereLink && (
                          <button
                            type="button"
                            className="training-history-differe-link"
                            title="Voir cette donne dans les résultats différé"
                            onClick={() => handleOpenDiffereSource(entry)}
                          >
                            ↩
                          </button>
                        )}
                        <button
                          type="button"
                          className="training-history-delete"
                          title="Supprimer de l'historique"
                          onClick={() => handleDeleteHistoryEntry(entry.id)}
                        >
                          ✕
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================================
  // Active game
  // ============================================

  void handleReady;

  // Contenu de la sidebar gauche (réglages + professeur), partagé entre
  // l'affichage colonne (wide/medium) et le drawer (compact).
  const trainingControlsNode = (
    <TrainingControls
      socket={socketRef.current}
      displayMode={displayMode}
      showTimeline={showTimeline}
      disabledBots={disabledBots}
      humanAutomated={humanAutomated}
      phase={gameState.phase}
      currentPlayer={gameState.currentPlayer}
      myPosition={gameState.myPosition}
      isWaitingRebid={isWaitingRebid}
      players={displayPlayers}
      canAskAdvice={canAskAdvice}
      gameSpeed={gameSpeed}
      coachMessages={coachMessages}
      onAddCoachMessage={handleAddCoachMessage}
      adviceDetailed={adviceDetailed}
      onToggleAdviceDetailed={() => setAdviceDetailed(prev => !prev)}
      onToggleDisplayMode={handleToggleDisplayMode}
      onToggleTimeline={handleToggleTimeline}
      onRotateBoard={handleRotateBoard}
      onToggleBot={handleToggleBot}
      onNewDeal={handleNewDeal}
      onRestartSameDeal={handleRestartSameDeal}
      onAbandon={handleAbandon}
      onRebidAfterPass={handleRebidAfterPass}
      onForceBid={handleForceBid}
      onSpeedChange={handleSpeedChange}
      canShowFeedback={canShowFeedback}
      feedbackMode={feedbackMode}
      onToggleFeedbackMode={handleToggleFeedbackMode}
      mlSuggestion={mlSuggestion}
      feedbackSeat={mlSuggestion?.seat ?? gameState.currentPlayer}
      legalMoves={
        mlSuggestion?.legal_moves
          ?? gameState.legalCards[mlSuggestion?.seat ?? gameState.currentPlayer]
          ?? []
      }
      hand={gameState.allHands[mlSuggestion?.seat ?? gameState.currentPlayer] ?? []}
      trickIdx={gameState.numPli}
      positionInTrick={gameState.currentTrick.length}
      gameState={{}}
      onSubmitFeedback={handleSubmitFeedback}
      feedbackCardClickRef={feedbackCardClickRef}
      onAddToLibrary={session.compet ? () => {
        setLibraryPartialSave(true);
        setShowLibraryModal(true);
      } : undefined}
      useBetaModel={preferences.useBetaModel}
      botModelSlot={botModelSlot}
      onToggleBotModelSlot={handleToggleBotModelSlot}
      isAdmin={isAdmin}
      aiModelSelection={aiModelSelection}
      onAIModelSelectionChange={setAIModelSelection}
      dealSetup={dealSetup}
      onDealSetupChange={handleDealSetupChange}
      taker={gameState.taker}
      viewPosition={viewPosition}
      onHideLeftColumn={!isCompact ? () => setTrainingLeftColumnHidden(true) : undefined}
      compareReplayBatch={
        compareReplayBatchEnriched && compareReplayBatchHasDealList(compareReplayBatchEnriched.deals)
          ? {
              title: compareReplayBatchEnriched.title,
              deals: compareReplayBatchEnriched.deals.map(d => ({
                dealIndex: d.dealIndex,
                label: d.label,
                variants: d.variants?.map(v => ({
                  tableIdx: v.tableIdx,
                  label: v.label,
                  scoreText: v.scoreText,
                  playerLabel: v.playerLabel,
                  teamLabel: v.teamLabel,
                })),
              })),
              selectedIndex: compareReplayBatchEnriched.selectedIndex,
              selectedVariantIndex: compareReplayBatchEnriched.selectedVariantIndex,
              dualSelectBothVariants: showReferencePane && linkedDualTables,
            }
          : null
      }
      onSelectCompareReplayDeal={
        compareReplayBatchEnriched && compareReplayBatchHasDealList(compareReplayBatchEnriched.deals)
          ? handleSelectCompareReplayDeal
          : undefined
      }
    />
  );

  return (
    <div className={`training-layout ${isCompact ? 'training-layout-compact' : ''}`}>
      {/* Left sidebar: controls
          - Wide / medium : colonne fixe à gauche
          - Compact : exposée via SideDrawer (FAB en bas-gauche) */}
      {isCompact ? (
        <SideDrawer
          side="left"
          fabIcon={<SettingsIcon size={22} />}
          fabLabel="Réglages d'entraînement"
          title="Entraînement"
        >
          {trainingControlsNode}
        </SideDrawer>
      ) : (
        trainingLeftColumnHidden ? null : (
          <div className="training-sidebar-left">
            {trainingControlsNode}
          </div>
        )
      )}

      {/* Center: game board */}
      <div className={`training-board-area ${referencePaneVisible ? 'training-board-area--dual' : ''}`}>
        <button
          type="button"
          className="training-home-close-btn"
          onClick={handleCloseToHome}
          title="Retour à l'accueil entraînement"
          aria-label="Retour à l'accueil entraînement"
        >
          <CloseIcon size={18} />
        </button>
        {referenceHistory && (
          <button
            type="button"
            className={`training-reference-toggle ${referencePaneVisible ? 'is-active' : ''}`}
            onClick={() => setShowReferencePane((v) => !v)}
            title={referencePaneVisible
              ? "Masquer la table de l'autre camp"
              : "Afficher la table de l'autre camp (donne duplicate)"}
          >
            {referencePaneVisible ? '⬛ Une table' : '⬛⬛ Deux tables'}
          </button>
        )}
        {pollMode && (
          <PollVoteBanner
            isMyTurn={isMyTurn || isEcart}
            partialPhase={pollMode.partialPhase}
            hasVoted={pollHasVoted}
          />
        )}
        {activeModelSlot === 'beta' && (
          <div className="training-model-badge training-model-badge--beta">
            <span className="training-model-badge-dot" />
            BETA
          </div>
        )}
        {activeModelSlot === 'legacy-rv' && (
          <div className="training-model-badge training-model-badge--beta">
            <span className="training-model-badge-dot" />
            LEGACY RV
          </div>
        )}
        <div className={`training-dual-panes ${referencePaneVisible ? 'is-dual' : ''}`}>
        <div className={`replay-pane training-live-pane ${referencePaneVisible ? '' : 'training-live-pane--solo'}`}>
          {referencePaneVisible && leftTeamHeader?.attackTeam && leftTeamHeader.defenseTeam && (
            <TrainingTeamTableHeader
              attackTeam={leftTeamHeader.attackTeam}
              defenseTeam={leftTeamHeader.defenseTeam}
            />
          )}
          <div className="replay-pane-body">
            <TrainingBoard
              displayMode={displayMode}
              viewPosition={viewPosition}
              players={displayPlayers}
              allHands={gameState.allHands}
              reviewHands={reviewHands}
              legalCards={gameState.legalCards}
              currentPlayer={gameState.currentPlayer}
              currentTrick={
                gameState.currentTrick.length > 0
                  ? gameState.currentTrick
                  : (lastCompletedTrick?.cards ?? [])
              }
              trickPlayerPositions={
                gameState.currentTrick.length > 0
                  ? gameState.trickPlayerPositions
                  : (lastCompletedTrick?.playerPositions ?? [])
              }
              phase={gameState.phase}
              taker={gameState.taker}
              currentContract={gameState.highestBid}
              completedTricks={gameState.completedTricks}
              disabledBots={disabledBots}
              humanAutomated={humanAutomated}
              randomFallbackCards={randomFallbackCards}
              reviewTrick={reviewTrick}
              reviewPhase={reviewPhase}
              reviewAnimIndex={reviewAnimIndex}
              onClearReview={() => { setReviewTrick(null); setReviewPhase(null); }}
              onRewindCard={handleRewindCard}
              socket={socketRef.current}
              onPlayCard={handlePlayCard}
              ecartMode={isEcart && !showChienOverlay && takerIsHumanControlled}
              ecartSelectedCards={ecartSelectedCards}
              discardableCards={discardableCards}
              onToggleEcartCard={handleToggleEcartCard}
              humanControlledPositions={humanControlledPositions}
              onBoardClick={gameSpeed === 'click' ? handleBoardClick : undefined}
              gameSpeed={gameSpeed}
              hasTopBanner={isEndOfDeal && !!gameState.scores}
              awaitingAdvance={gameSpeed === 'click' && (!!lastCompletedTrick || awaitingAdvance)}
              highlightedCard={highlightedCard}
              feedbackSeat={feedbackMode && isPlaying ? (mlSuggestion?.seat ?? gameState.currentPlayer) : null}
              mlSuggestedCards={mlSuggestedCards}
              displayedTrickIsCompleted={
                gameState.currentTrick.length === 0 && lastCompletedTrick
                  ? gameState.completedTricks.length
                  : undefined
              }
              metricHighlightKeys={metricHighlightKeySet}
              metricTableTrickNumber={metricTableTrickNumber}
              onCommentCard={libraryGameId ? handleCommentCard : undefined}
              onCommentTrick={libraryGameId ? handleCommentTrick : undefined}
              feedbackEnabledSeats={feedbackEnabledSeats}
              onToggleFeedbackSeat={handleToggleFeedbackSeat}
              feedbackMode={feedbackMode}
            />
          </div>
        </div>
        {referencePaneVisible && referenceHistory && referencePosition && (
          <ReplayPane
            className="training-reference-pane"
            history={referenceHistory}
            stepIndex={referencePosition.stepIndex}
            subStep={referencePosition.subStep}
            header={
              rightTeamHeader?.attackTeam && rightTeamHeader.defenseTeam
                ? (
                  <TrainingTeamTableHeader
                    attackTeam={rightTeamHeader.attackTeam}
                    defenseTeam={rightTeamHeader.defenseTeam}
                  />
                )
                : undefined
            }
            masked={!exerciseMistakeMode && !isEndOfDeal && !revealReference}
            maskMessage={exerciseMistakeMode
              ? 'Table de référence (meilleure bifurcation).'
              : 'Les mains sont identiques aux vôtres : la table de l\'autre camp reste masquée jusqu\'à la fin de la donne.'}
            maskActionLabel="Révéler quand même"
            onReveal={() => setRevealReference(true)}
          />
        )}
        </div>

        {/* Bidding overlay — shows all bids and controls */}
        {isBidding && (
          <div className={`training-bidding-overlay ${isMyTurn ? 'my-turn' : ''}`}>
            <div className="training-bidding-title">Enchères</div>

            <div className="training-bids-list">
              {gameState.bids.map((b, i) => {
                const pName = displayPlayers.find(pl => pl.position === b.player)?.pseudo ?? `P${b.player}`;
                return (
                  <div
                    key={i}
                    className="training-bid-entry"
                    onContextMenu={libraryGameId ? (e) => {
                      e.preventDefault();
                      handleCommentBid(i, b.player, b.bid, e);
                    } : undefined}
                  >
                    <span className="bid-player">{pName}</span>
                    <span className={`bid-value ${b.bid === 0 ? 'pass' : 'bid'}`}>
                      {bidLabel(b.bid)}
                    </span>
                  </div>
                );
              })}
              {/* Show "attente" for the current bidder */}
              <div className="training-bid-entry training-bid-pending">
                <span className="bid-player">{bidderName}</span>
                <span className="bid-value pending">attente</span>
              </div>
            </div>

            {isMyTurn ? (
              <>
                <div className="training-bidding-prompt">
                  À <strong>{bidderName}</strong> d'enchérir
                </div>
                <div className="training-bidding-buttons">
                  <button className="training-btn training-btn-secondary" onClick={() => handleBid(0)}>Passe</button>
                  {gameState.highestBid < 1 && maxBidAllowed >= 1 && <button className="training-btn training-btn-primary" onClick={() => handleBid(1)}>Petite</button>}
                  {gameState.highestBid < 2 && maxBidAllowed >= 2 && <button className="training-btn training-btn-primary" onClick={() => handleBid(2)}>Garde</button>}
                  {gameState.highestBid < 3 && maxBidAllowed >= 3 && <button className="training-btn training-btn-primary" onClick={() => handleBid(3)}>Garde sans</button>}
                  {gameState.highestBid < 4 && maxBidAllowed >= 4 && <button className="training-btn training-btn-primary" onClick={() => handleBid(4)}>Garde contre</button>}
                </div>
              </>
            ) : (
              <div className="training-bidding-waiting">
                En attente de <strong>{bidderName}</strong>
              </div>
            )}
          </div>
        )}

        {/* Chien display overlay */}
        {isEcart && showChienOverlay && gameState.chien && gameState.chien.length > 0 && (
          <div className="training-chien-overlay" onClick={handleDismissChien}>
            <div className="training-chien-title">Le Chien</div>
            <div className="training-chien-cards">
              {gameState.chien.map((card, i) => (
                <div key={`chien-${card}-${i}`} className="training-chien-card">
                  <Card card={card} size="medium" />
                </div>
              ))}
            </div>
            <div className="training-chien-hint">Cliquez pour continuer</div>
          </div>
        )}

        {/* Écart selection UI */}
        {isEcart && !showChienOverlay && takerIsHumanControlled && (
          <div className="training-ecart-overlay">
            <div className="training-ecart-panel">
              <div className="training-ecart-title">
                Écart : {ecartSelectedCards.length}/6 cartes
              </div>
              {ecartSelectedCards.length > 0 && (
                <div className="training-ecart-hint">
                  Cliquez sur une carte ci-dessous pour la remettre dans la main
                </div>
              )}
              <div className="training-ecart-selected">
                {ecartSelectedCards.length === 0 ? (
                  <span style={{ color: 'rgba(255,255,255,0.5)', padding: '0.5rem 1rem' }}>
                    Cliquez sur les cartes de la main du preneur à écarter
                  </span>
                ) : (
                  ecartSelectedCards.map((card) => (
                    <div
                      key={`ecart-sel-${card}`}
                      className="training-ecart-selected-card"
                      onClick={() => handleToggleEcartCard(card)}
                    >
                      <Card card={card} size="small" />
                    </div>
                  ))
                )}
              </div>
              <div className="training-ecart-actions">
                <button
                  className="training-btn training-btn-primary"
                  disabled={ecartSelectedCards.length !== 6}
                  onClick={handleValidateEcart}
                >
                  Valider l'écart
                </button>
                {ecartSelectedCards.length > 0 && (
                  <button
                    className="training-btn training-btn-secondary"
                    onClick={() => setEcartSelectedCards([])}
                  >
                    Annuler
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Rebid banner */}
        {isWaitingRebid && (
          <div className="training-rebid-banner">
            <h3>Tout le monde a passé</h3>
            {gameState.chien && (
              <p>Chien : {gameState.chien.length} cartes visibles dans les contrôles</p>
            )}
            <div className="training-rebid-actions">
              <button className="training-btn training-btn-primary" onClick={handleRebidAfterPass}>
                Relancer les enchères
              </button>
              <button className="training-btn training-btn-secondary" onClick={() => handleNewDeal()}>
                Nouvelle donne
              </button>
            </div>
          </div>
        )}

        {/* Bid comparison (passive feedback) */}
        {bidComparison && (
          <div className={`training-comparison-banner ${bidComparison.agrees ? 'agree' : 'disagree'}`}>
            <div className="comparison-icon">{bidComparison.agrees ? '✓' : '≠'}</div>
            <div className="comparison-content">
              <strong>Enchère</strong>
              {bidComparison.agrees
                ? <span> — L'IA est d'accord avec votre choix ({bidComparison.mlBidName})</span>
                : <span> — L'IA aurait choisi <strong>{bidComparison.mlBidName}</strong></span>
              }
              {!bidComparison.agrees && bidComparison.probabilities && Object.keys(bidComparison.probabilities).length > 0 && (
                <span className="comparison-probs">
                  {Object.entries(bidComparison.probabilities)
                    .sort(([,a], [,b]) => b - a)
                    .slice(0, 3)
                    .map(([name, prob]) => `${name}: ${(prob * 100).toFixed(0)}%`)
                    .join(' · ')}
                </span>
              )}
              {bidComparison.source === 'heuristic' && <span className="comparison-source">(heuristique)</span>}
            </div>
            <button className="comparison-close" onClick={() => setBidComparison(null)}>✕</button>
          </div>
        )}

        {/* Discard comparison (passive feedback) */}
        {discardComparison && (
          <div className={`training-comparison-banner ${discardComparison.agrees ? 'agree' : 'disagree'}`}>
            <div className="comparison-icon">{discardComparison.agrees ? '✓' : `${discardComparison.overlap}/6`}</div>
            <div className="comparison-content">
              <strong>Écart</strong>
              {discardComparison.agrees
                ? <span> — L'IA est d'accord avec votre écart</span>
                : <span> — L'IA aurait écarté : {discardComparison.mlDiscardNames.join(', ')}</span>
              }
              {discardComparison.source === 'heuristic' && <span className="comparison-source">(heuristique)</span>}
            </div>
            <button className="comparison-close" onClick={() => setDiscardComparison(null)}>✕</button>
          </div>
        )}

        {/* End of deal: compact score banner + review controls */}
        {isEndOfDeal && gameState.scores && (
          <>
            <div className="training-end-banner">
              <div className="training-end-banner-score">
                <span className={`training-end-result ${gameState.scores.won ? 'won' : 'lost'}`}>
                  {gameState.scores.won ? 'Contrat réussi' : 'Contrat chuté'}
                </span>
                <span className="training-end-detail">
                  {gameState.scores.takerPoints} pts
                  (besoin de {gameState.scores.requiredPoints} — {gameState.scores.oudlers} bout{gameState.scores.oudlers !== 1 ? 's' : ''})
                </span>
              </div>
              <div className="training-end-banner-actions">
                <button className="training-btn training-btn-secondary training-btn-sm" onClick={handleReviewPrev}>
                  ◀ Pli préc.
                </button>
                <button className="training-btn training-btn-secondary training-btn-sm" onClick={handleReviewNext}>
                  Pli suiv. ▶
                </button>
                <button
                  className="training-btn training-btn-primary training-btn-sm"
                  onClick={() => setShowEndOfDealMenu(prev => !prev)}
                >
                  Suite ▾
                </button>
              </div>
            </div>
            {showEndOfDealMenu && (
              <div className="training-end-menu">
                <button className="library-basket-btn" onClick={() => { setShowEndOfDealMenu(false); setLibraryPartialSave(false); setShowLibraryModal(true); }}>
                  📚 Ajouter à la bibliothèque
                </button>
                <button className="training-btn training-btn-primary" onClick={() => { setShowEndOfDealMenu(false); handleNewDeal(); }}>
                  Nouvelle donne
                </button>
                <button className="training-btn training-btn-secondary" onClick={() => { setShowEndOfDealMenu(false); handleRestartSameDeal(); }}>
                  Rejouer cette donne
                </button>
                <button className="training-btn training-btn-danger" onClick={handleAbandon}>
                  Quitter l'entraînement
                </button>
              </div>
            )}
          </>
        )}

        {/* Position picker overlay */}
        {showPositionPicker && (
          <div className="training-position-picker-overlay">
            <div className="training-position-picker">
              <h3>Rejouer depuis quelle position ?</h3>
              <div className="training-position-picker-grid">
                {[0, 1, 2, 3].map(pos => {
                  const posNames = ['Sud', 'Est', 'Nord', 'Ouest'];
                  const player = displayPlayers.find(p => p.position === pos);
                  const isCurrent = pos === activePosition;
                  return (
                    <button
                      key={pos}
                      className={`training-btn ${isCurrent ? 'training-btn-primary' : 'training-btn-secondary'}`}
                      onClick={() => handleRestartWithPosition(pos)}
                    >
                      {posNames[pos]}
                      <span className="training-position-picker-sub">
                        {player?.pseudo ?? `P${pos}`}
                        {isCurrent ? ' (actuel)' : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
              <button
                className="training-btn training-btn-secondary"
                onClick={() => setShowPositionPicker(false)}
                style={{ marginTop: 8 }}
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Right sidebar: trick timeline
          - Wide : colonne fixe à droite
          - Compact : exposée via SideDrawer (FAB en bas-droite) */}
      {showTimeline && (() => {
        const timelineNode = (
          <>
            <TrainingTimeline
              tricks={timelineTricks}
              currentTrickCards={currentTrickTimelineCards}
              currentTrickNumber={isPlaying && !lastCompletedTrick ? gameState.numPli + 1 : 0}
              selectedTrickNumber={reviewTrick?.trickNumber ?? null}
              selectedPhase={reviewPhase?.type ?? null}
              metricHighlightKeys={metricHighlightKeySet}
              metricHighlightTrickNumbers={metricHighlightTrickNumbers}
              bifurcationTrickNumbers={bifurcationTricks}
              myPosition={viewPosition}
              onTrickClick={handleTrickClick}
              onCurrentTrickClick={handleCurrentTrickClick}
              onPhaseClick={handlePhaseReview}
              onTrickContextMenu={libraryGameId ? (tn, e) => {
                e.preventDefault();
                handleCommentTrick(tn);
              } : undefined}
              onBidContextMenu={libraryGameId ? handleCommentBid : undefined}
              bids={gameState.bids}
              chien={gameState.chien}
              ecartCards={ecartCards}
              taker={gameState.taker}
              phase={gameState.phase}
              players={displayPlayers}
              highestBid={gameState.highestBid}
            />
            {pollMode && (
              <PollPanel
                pseudo={session.pseudo}
                sharedGameId={pollMode.sharedGameId}
                isAuthor={pollMode.sharedBy.toLowerCase() === session.pseudo.toLowerCase()}
                partialPhase={pollMode.partialPhase}
                refreshKey={pollHasVoted}
              />
            )}
            {libraryGameId && session.compet && !pollMode && (
              <GameCommentsPanel
                pseudo={session.pseudo}
                gameId={libraryGameId}
                gameSource={libraryGameSource}
                commentsEnabled={libraryCommentsEnabled}
                currentTrickNum={
                  reviewTrick?.trickNumber ??
                  (isPlaying && gameState.numPli > 0 ? gameState.numPli : undefined)
                }
                commentContext={commentingContext as CommentContext | null}
                onClearContext={() => setCommentingContext(null)}
              />
            )}
            {!libraryGameId && !pollMode && session.compet && gameState.phase !== 'waiting' && (
              <div className="library-comments-panel" style={{ opacity: 0.6 }}>
                <div className="library-comments-empty" style={{ padding: '12px' }}>
                  Enregistrez la partie dans la bibliothèque pour activer les commentaires
                </div>
              </div>
            )}
          </>
        );
        return isCompact ? (
          <SideDrawer
            side="right"
            fabIcon={<ListGridIcon size={22} />}
            fabLabel="Historique des plis"
            title="Historique"
          >
            {timelineNode}
          </SideDrawer>
        ) : (
          <div className="training-sidebar-right">
            {timelineNode}
          </div>
        );
      })()}

      {/* Toast notifications (admin only) */}
      {isAdmin && mlToast && (
        <div className="training-toast-container">
          <div className={`training-toast training-toast-${mlToast.type} ${mlToast.exiting ? 'training-toast-exit' : ''}`}>
            <span className="training-toast-icon">
              {mlToast.type === 'error' ? '⚠' : '✓'}
            </span>
            <span>
              {mlToast.type === 'error'
                ? 'Connexion avec l\'IA interrompue — jouerie aléatoire'
                : 'Connexion avec l\'IA rétablie'}
            </span>
          </div>
        </div>
      )}

      {pollAfterVote && pollMode && (
        <PollAfterVoteModal
          pseudo={session.pseudo}
          sharedGameId={pollMode.sharedGameId}
          voteType={pollAfterVote.voteType}
          voteLabel={pollAfterVote.voteLabel}
          responses={pollAfterVote.responses}
          onContinue={() => setPollAfterVote(null)}
          onBackToLibrary={() => {
            setPollAfterVote(null);
            setPollMode(null);
            setPollHasVoted(false);
            setActiveTab('library');
          }}
        />
      )}

      {showLibraryModal && (() => {
        const history = buildTrainingGameHistory(libraryPartialSave);
        if (!history) return null;
        const partialPhaseNum = libraryPartialSave
          ? (gameState.phase === 'bidding' ? 1 : gameState.phase === 'ecart' ? 2 : 3)
          : undefined;
        const partialTrick = libraryPartialSave
          ? gameState.completedTricks.length + (gameState.currentTrick.length > 0 ? 1 : 0)
          : undefined;
        return (
          <AddToLibraryModal
            pseudo={session.pseudo}
            history={history}
            originType="training"
            myPosition={0}
            isPartial={libraryPartialSave || undefined}
            partialPhase={partialPhaseNum}
            partialTrickNum={partialTrick}
            onClose={() => { setShowLibraryModal(false); setLibraryPartialSave(false); }}
            onSaved={(id) => {
              setLibraryGameId(id);
              setLibraryGameSource('own');
              setLibraryCommentsEnabled(true);
            }}
          />
        );
      })()}
    </div>
  );
}

function bidLabel(bid: number): string {
  switch (bid) {
    case 0: return 'Passe';
    case 1: return 'Petite';
    case 2: return 'Garde';
    case 3: return 'Garde sans';
    case 4: return 'Garde contre';
    default: return String(bid);
  }
}
